import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useData } from "../contexts/DataContext";
import { useSmartBack } from "../hooks/useSmartBack";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Tooltip as ShadTooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "../components/ui/tooltip";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import TimePicker12 from "../components/TimePicker12";
import DatePickerField from "../components/DatePickerField";
import AutosaveStatus from "../components/AutosaveStatus";
import LastEditedLabel from "../components/LastEditedLabel";
import SmsConsentToggle from "../components/SmsConsentToggle";
import { DetailPageSkeleton } from "../components/skeletons";
import { useAutosave } from "../hooks/useAutosave";
import {
  ArrowLeft, Save, Loader2, User, MapPin, DollarSign, Clock,
  Camera, CalendarDays, FileText, CheckSquare, Ruler, Receipt,
  Briefcase, CreditCard, PiggyBank, Workflow, ShoppingCart, Plus,
  ExternalLink, UserPlus, Trash2, TrendingUp, Percent, ClipboardCheck,
  Zap, Phone, Mail, Hash, Building2, Eye, Send, Link2, CreditCard as CreditCardIcon, X, CheckCircle2, Pencil, RefreshCw, MessageSquare
} from "lucide-react";
import PhotoUpload from "../components/PhotoUpload";
import ProjectAutomations from "../components/ProjectAutomations";
import ProjectEmails from "../components/ProjectEmails";
import SendInvoiceDialog from "../components/SendInvoiceDialog";
import MeasurementCalculator from "./MeasurementCalculator";
import { useAuth } from "../contexts/AuthContext";
import { fmtTime12 } from "../lib/time";
import { isQuoteExpired } from "../lib/quoteExpiry";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

function ActivityTimeline({ activities }) {
  if (!activities.length) return <p className="text-center py-6 text-slate-400 text-sm">No activity yet</p>;
  return (
    <div className="space-y-4">
      {activities.slice(0, 25).map(activity => {
        const colorMap = { created: "bg-emerald-500", stage_changed: "bg-blue-500", photo_uploaded: "bg-purple-500", email_sent: "bg-amber-500", customer_signed: "bg-emerald-500", status_changed: "bg-blue-500", updated: "bg-slate-400" };
        const dotColor = colorMap[activity.action] || "bg-[#00A3E0]";
        const relTime = (() => {
          const diff = Math.floor((Date.now() - new Date(activity.created_at)) / 1000);
          if (diff < 60) return "just now";
          if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
          if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
          if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
          return new Date(activity.created_at).toLocaleDateString();
        })();
        return (
          <div key={activity.id} className="flex gap-3" data-testid={`project-activity-${activity.id}`}>
            <div className="flex flex-col items-center">
              <div className={`w-2.5 h-2.5 rounded-full ${dotColor} flex-shrink-0 mt-1.5`} />
              <div className="w-px flex-1 bg-slate-200 mt-1" />
            </div>
            <div className="flex-1 pb-4">
              <p className="text-sm text-slate-700">{activity.details}</p>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs text-slate-500 font-medium">{activity.user_name || "System"}</span>
                <span className="text-xs text-slate-300">&middot;</span>
                <span className="text-xs text-slate-400">{relTime}</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TabPlaceholder({ icon: Icon, title, description }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-slate-400">
      <Icon className="w-10 h-10 mb-3 text-slate-300" />
      <p className="font-medium text-slate-500">{title}</p>
      <p className="text-sm mt-1">{description}</p>
    </div>
  );
}

const fmtCurrency = (v) => `$${(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// ======================== QUOTE TAB WITH MEASUREMENT REPORT SELECTION ========================

function QuoteTabContent({ projectId, contactId, projectQuotes, quoteTemplates, roofReports, createQuote, deleteQuote, navigate, fmtCurrency, onQuotesChanged }) {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedReport, setSelectedReport] = useState("");
  const [creating, setCreating] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const handleCreateFromTemplate = async () => {
    if (!selectedTemplate) return;
    setCreating(true);
    try {
      const payload = {
        project_id: projectId,
        contact_id: contactId,
        template_id: selectedTemplate.id,
      };
      if (selectedReport) payload.roof_report_id = selectedReport;
      const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/quotes/from-template`, payload);
      toast.success("Quote created with pricing!");
      navigate(`/quotes/${res.data.id}?from=project&projectId=${projectId}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create quote");
    }
    setCreating(false);
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Quotes</CardTitle>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="h-9 text-sm" onClick={() => setShowCreateModal(true)} data-testid="create-quote-from-template">
              <FileText className="w-3.5 h-3.5 mr-1" />From Template
            </Button>
            <Button size="sm" className="bg-[#0F4C81] hover:bg-[#0D4270] h-9" onClick={() => { createQuote({ project_id: projectId, contact_id: contactId, status: "draft" }).then(q => { toast.success("New quote!"); navigate(`/quotes/${q.id}?from=project&projectId=${projectId}`); }).catch(() => toast.error("Failed")); }} data-testid="create-quote-scratch-btn"><Plus className="w-4 h-4 mr-1" />Blank</Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {projectQuotes.length > 0 ? (
          <div className="space-y-2">
            {projectQuotes.map(q => {
              const tpl = q.template_id ? (quoteTemplates || []).find(t => t.id === q.template_id) : null;
              const tplName = tpl?.name || q.notes_type || "Quote";
              return (
                <div key={q.id} className="flex items-center justify-between p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/quotes/${q.id}?from=project&projectId=${projectId}`)} data-testid={`project-quote-${q.id}`}>
                  <div className="flex items-center gap-3">
                    <FileText className="w-4 h-4 text-[#0F4C81]" />
                    <div>
                      <p className="text-sm font-medium text-slate-700">
                        <span>{q.quote_number || "Draft"}</span>
                        <span className="text-slate-400"> · </span>
                        <span className="text-[#0F4C81]">{tplName}</span>
                      </p>
                      <p className="text-xs text-slate-400">{new Date(q.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {isQuoteExpired(q) && (
                      <Button size="sm" variant="outline" className="h-6 px-2 text-[10px] border-amber-300 text-amber-700 hover:bg-amber-50" data-testid={`reopen-quote-${q.id}`} onClick={(e) => { e.stopPropagation(); axios.post(`${API_URL}/quotes/${q.id}/reopen`).then((res) => { toast.success(`Reopened — valid until ${new Date(res.data.valid_until + "T12:00:00").toLocaleDateString()}`); onQuotesChanged?.(); }).catch(() => toast.error("Failed to reopen")); }}>Reopen</Button>
                    )}
                    <Badge variant={q.status === "approved" ? "default" : "outline"} className={`text-xs capitalize ${isQuoteExpired(q) ? "border-amber-300 text-amber-700" : ""}`}>{isQuoteExpired(q) ? "expired" : q.status}</Badge>{confirmDeleteId === q.id ? (<div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}><Button size="sm" className="h-6 px-2 text-[10px] bg-red-600 hover:bg-red-700 text-white" onClick={(e) => { e.stopPropagation(); deleteQuote(q.id).then(() => { toast.success("Moved to trash"); setConfirmDeleteId(null); }).catch(() => toast.error("Failed to delete")); }}>Delete</Button><Button variant="outline" size="sm" className="h-6 px-2 text-[10px]" onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(null); }}>Cancel</Button></div>) : (<Button variant="ghost" size="sm" className="text-red-400 hover:text-red-600 h-7 w-7 p-0" data-testid={`delete-quote-${q.id}`} onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(q.id); }}><Trash2 className="w-3.5 h-3.5" /></Button>)}</div>
                </div>
              );
            })}
          </div>
        ) : <div className="text-center py-10 text-slate-400"><Receipt className="w-8 h-8 mx-auto mb-2 text-slate-300" /><p className="text-sm">No quotes yet</p></div>}
      </CardContent>

      {/* Create from Template Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={() => setShowCreateModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-[500px] max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-5 border-b">
              <h3 className="text-lg font-bold text-slate-800">Create Quote from Template</h3>
              <p className="text-sm text-slate-500 mt-1">Select a template and optionally attach a measurement report to auto-fill quantities.</p>
            </div>
            <div className="p-5 space-y-5">
              {/* Template Selection */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Quote Template *</Label>
                <div className="space-y-1.5">
                  {quoteTemplates.length > 0 ? quoteTemplates.map(t => (
                    <button key={t.id} type="button" onClick={() => setSelectedTemplate(t)}
                      className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all ${selectedTemplate?.id === t.id ? "border-[#0F4C81] bg-[#0F4C81]/5" : "border-slate-200 hover:border-slate-300"}`}
                      data-testid={`select-template-${t.id}`}
                    >
                      <p className="text-sm font-medium text-slate-700">{t.name}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{(t.pages || []).length} pages &middot; {(t.pages || []).filter(p => p.page_type === "option").length} options</p>
                    </button>
                  )) : <p className="text-sm text-slate-400 py-4 text-center">No templates created yet</p>}
                </div>
              </div>

              {/* Measurement Report Selection */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Measurement Report (optional)</Label>
                <p className="text-xs text-slate-500">Selecting a report will auto-fill line item quantities from your measurements.</p>
                <div className="space-y-1.5">
                  <button type="button" onClick={() => setSelectedReport("")}
                    className={`w-full text-left px-4 py-2.5 rounded-lg border-2 transition-all ${!selectedReport ? "border-[#0F4C81] bg-[#0F4C81]/5" : "border-slate-200 hover:border-slate-300"}`}
                  >
                    <p className="text-sm text-slate-600">No measurement report (manual quantities)</p>
                  </button>
                  {roofReports.length > 0 ? roofReports.map(r => (
                    <button key={r.id} type="button" onClick={() => setSelectedReport(r.id)}
                      className={`w-full text-left px-4 py-2.5 rounded-lg border-2 transition-all ${selectedReport === r.id ? "border-[#00A3E0] bg-[#00A3E0]/5" : "border-slate-200 hover:border-slate-300"}`}
                      data-testid={`select-report-${r.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-slate-700">{r.name || "Roof Report"}{r.project_id === projectId ? "" : r.project_id ? " (other project)" : " (unlinked)"}</p>
                          <p className="text-xs text-slate-400">{(r.structures || []).length} structures &middot; {new Date(r.created_at).toLocaleDateString()}</p>
                        </div>
                        <Badge variant={r.status === "complete" ? "default" : "outline"} className="text-[10px] capitalize">{r.status || "draft"}</Badge>
                      </div>
                    </button>
                  )) : <p className="text-xs text-slate-400 text-center py-2">No roof reports found</p>}
                </div>
              </div>
            </div>
            <div className="p-5 border-t flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
              <Button className="bg-[#0F4C81] hover:bg-[#0D4270]" disabled={!selectedTemplate || creating} onClick={handleCreateFromTemplate} data-testid="confirm-create-quote">
                {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                Create Quote
              </Button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { goBack } = useSmartBack();
  const {
    updateProject, contacts, fetchContacts,
    dropdownOptions, fetchDropdownOptions,
    pipelineStages, fetchPipelineStages,
    pipelineCategories, fetchPipelineCategories,
    fetchActivities,
    quotes, fetchQuotes, createQuote, deleteQuote,
    quoteTemplates, fetchQuoteTemplates,
    tasks, fetchTasks, createTask,
    deleteProject
  } = useData();

  const { user } = useAuth();

  const [project, setProject] = useState(null);
  const [activities, setActivities] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [events, setEvents] = useState([]);
  const [roofReports, setRoofReports] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [budget, setBudget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({});

  // Modals
  const [showEventModal, setShowEventModal] = useState(false);
  const [editingEventId, setEditingEventId] = useState(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [editingInvoiceId, setEditingInvoiceId] = useState(null);
  const [editInvoiceForm, setEditInvoiceForm] = useState({});
  const [invoicePreview, setInvoicePreview] = useState(null);
  const [showCostModal, setShowCostModal] = useState(false);
  const [sendInvoiceDialog, setSendInvoiceDialog] = useState({ open: false, invoiceId: "", invoiceNumber: "" });
  // Confirmation modal for moving a project to "Paid & Closed" — lets
  // the admin opt out of review-request pings or silence the entire
  // long-tail nurture flow (for jobs that had friction).
  const [paidClosedDialog, setPaidClosedDialog] = useState({ open: false, pendingStage: "", skipReview: false, disableOutreach: false });
  const [activeTab, setActiveTab] = useState("review");
  const [emailBounceCount, setEmailBounceCount] = useState(0);

  const [eventForm, setEventForm] = useState({ title: "", event_type_id: "", start_date: "", start_time: "08:00", end_time: "17:00", notes: "", assigned_to: "" });
  const [eventTypes, setEventTypes] = useState([]);
  const [teamUsers, setTeamUsers] = useState([]);
  const [taskForm, setTaskForm] = useState({ title: "", priority: "medium", due_date: "", notes: "" });
  const [invoiceForm, setInvoiceForm] = useState({ invoice_type: "deposit", deposit_mode: "percent", title: "", description: "", amount: "", due_date: "", quote_id: "", line_items: [], discount: 0, deposit_percent: null, payments_applied: 0 });

  const invoiceTitlePresets = ["Roof Install", "Roof Repair", "Fence Install", "Fence Repair", "Gutter Install", "Gutter Repair", "Siding Install", "Siding Repair", "Window Install", "Window Repair", "Deck Build", "Deck Repair", "General Repair", "Maintenance"];
  const [paymentForm, setPaymentForm] = useState({ amount: "", method: "check", reference: "", invoice_id: "", date: new Date().toISOString().slice(0, 10), silent: false });
  const [costForm, setCostForm] = useState({ name: "", amount: "", cost_type: "material", notes: "" });
  const [editingCostId, setEditingCostId] = useState(null);
  const [editCostForm, setEditCostForm] = useState({ name: "", amount: "", cost_type: "material", notes: "" });

  const serviceTypes = dropdownOptions.service_types || [];
  const leadSources = dropdownOptions.lead_sources || [];
  const classifications = dropdownOptions.classifications || [];

  const loadProject = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/projects/${id}`);
      setProject(response.data);
      setFormData(response.data);
    } catch {
      toast.error("Project not found");
      navigate("/projects");
    }
    setLoading(false);
  }, [id, navigate]);

  const loadBudget = useCallback(async () => {
    try { const res = await axios.get(`${API_URL}/project-budget/${id}`); setBudget(res.data); }
    catch { /* no budget data */ }
  }, [id]);

  const loadEvents = useCallback(async () => {
    try { const res = await axios.get(`${API_URL}/schedule?project_id=${id}`); setEvents(res.data); }
    catch { /* no events */ }
  }, [id]);

  const loadEventTypes = useCallback(async () => {
    try { const res = await axios.get(`${API_URL}/event-types`); setEventTypes(res.data || []); }
    catch { /* ignore */ }
  }, []);

  const loadTeamUsers = useCallback(async () => {
    try { const res = await axios.get(`${API_URL}/auth/users`); setTeamUsers((res.data || []).filter(u => u.role !== "viewer")); }
    catch { /* may lack permission */ }
  }, []);

  const loadRoofReports = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/roof-reports?project_id=${id}`);
      setRoofReports(res.data);
    }
    catch { /* no reports */ }
  }, [id]);

  const loadInspections = useCallback(async () => {
    try { const res = await axios.get(`${API_URL}/inspections?project_id=${id}`); setInspections(res.data); }
    catch { /* no inspections */ }
  }, [id]);

  useEffect(() => {
    loadProject(); fetchContacts(); fetchDropdownOptions(); fetchPipelineStages(); fetchPipelineCategories();
    fetchQuotes(); fetchQuoteTemplates(); fetchTasks();
  }, [loadProject, fetchContacts, fetchDropdownOptions, fetchPipelineStages, fetchPipelineCategories, fetchQuotes, fetchQuoteTemplates, fetchTasks]);

  useEffect(() => {
    if (id) { fetchActivities("project", id).then(setActivities); loadEvents(); loadBudget(); loadRoofReports(); loadInspections(); }
  }, [id, fetchActivities, loadEvents, loadBudget, loadRoofReports, loadInspections]);

  useEffect(() => { loadEventTypes(); loadTeamUsers(); }, [loadEventTypes, loadTeamUsers]);

  useEffect(() => {
    if (id) axios.get(`${API_URL}/uploads/entity/project/${id}`).then(r => setPhotos(r.data)).catch(() => {});
  }, [id]);

  // Set (or clear) the project's hero photo. Passing `photo = null` clears
  // it. The absolute URL is stored server-side because Twilio needs a
  // publicly reachable HTTPS URL to attach as an MMS.
  const handleSetCoverPhoto = useCallback(async (photo) => {
    const rawUrl = photo?.url || "";
    // Strip any cache-buster (?v=…) so we save the canonical URL.
    const cleanRaw = rawUrl.split("?")[0];
    const absolute = cleanRaw
      ? (cleanRaw.startsWith("http") ? cleanRaw : `${process.env.REACT_APP_BACKEND_URL}${cleanRaw}`)
      : "";
    try {
      const res = await axios.patch(`${API_URL}/projects/${id}/cover-photo`, { cover_photo_url: absolute });
      setProject(res.data);
      setFormData(prev => ({ ...prev, cover_photo_url: absolute }));
      toast.success(absolute ? "Cover photo set" : "Cover photo removed");
      fetchActivities("project", id).then(setActivities);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update cover photo");
    }
  }, [id, fetchActivities]);

  // Bounce badge — fetch just the summary so we can flag bad customer addresses at a glance
  useEffect(() => {
    if (!id) return;
    axios.get(`${API_URL}/projects/${id}/emails`).then(r => {
      const s = r.data?.summary || {};
      setEmailBounceCount((s.bounced || 0) + (s.failed || 0));
    }).catch(() => setEmailBounceCount(0));
  }, [id, activeTab]);

  const contact = contacts.find(c => c.id === project?.contact_id);
  const projectTasks = tasks.filter(t => t.project_id === id);
  const projectQuotes = quotes.filter(q => q.project_id === id);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Strip `stage` from the manual Save payload — same reason as
      // autosave: the pipeline stage is server-owned via automations.
      // Manual stage changes go through the dedicated PATCH endpoint
      // wired to the Stage dropdown below.
      const { stage: _s, ...safe } = formData;
      const updated = await updateProject(id, { ...safe, estimated_value: parseFloat(safe.estimated_value) || 0 });
      setProject(updated);
      toast.success("Project updated!");
    } catch { toast.error("Failed to update project"); }
    setSaving(false);
  };

  // Manual stage change from the header dropdown. Hits the dedicated
  // PATCH endpoint so it doesn't collide with autosave and so backend
  // automations (workflow triggers, activity log) fire correctly.
  const handleStageChange = async (newStage) => {
    if (!newStage || newStage === formData.stage) return;
    // Optimistic local update so the dropdown reflects the choice instantly.
    setFormData(prev => ({ ...prev, stage: newStage }));
    setProject(prev => prev ? { ...prev, stage: newStage } : prev);
    try {
      const token = localStorage.getItem("token");
      await axios.patch(
        `${API_URL}/projects/${id}/stage?stage=${encodeURIComponent(newStage)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success("Stage updated");
    } catch {
      toast.error("Failed to update stage");
      // Re-hydrate from server to reflect the true state.
      try {
        const r = await axios.get(`${API_URL}/projects/${id}`);
        setProject(r.data);
        setFormData(r.data);
      } catch {}
    }
  };

  // Autosave — debounced 900ms after edits. Skips hydration and dedupes
  // against last saved snapshot. Doesn't toast on success (silent) but
  // exposes a status pill for the header. Never runs while the page is
  // still hydrating from the network. Excludes fields the server owns
  // (id, created_at, and CRUCIALLY `stage` — the pipeline stage is
  // driven by backend automations like on_appointment_booked and
  // on_quote_being_built. If we let autosave PUT the whole formData
  // including a stale local `stage`, we clobber any auto-move the
  // backend just made. Bug repro: open project → create event →
  // backend auto-moves stage to Appointment Scheduled → autosave
  // fires with stale stage → project bounces back to Inquiry Received.
  // Manual stage changes use their own PATCH endpoint below.)
  const { status: autosaveStatus, savedAt, saveNow } = useAutosave(
    formData,
    async (value) => {
      if (!value || !value.contact_id || !value.service_type) return;
      const { stage: _stageOwnedByBackend, ...safeValue } = value;
      const payload = { ...safeValue, estimated_value: parseFloat(safeValue.estimated_value) || 0 };
      const updated = await updateProject(id, payload);
      // Merge but keep the freshest stage from the server response —
      // never overwrite with our stale local copy.
      setProject((prev) => ({ ...(prev || {}), ...updated }));
    },
    { delay: 900, enabled: !loading && !!id },
  );

  // Flush on unload / route change so an in-flight edit is never lost.
  useEffect(() => {
    const flush = () => { saveNow(); };
    window.addEventListener("beforeunload", flush);
    return () => {
      window.removeEventListener("beforeunload", flush);
      flush();
    };
  }, [saveNow]);

  const getStageInfo = (stageValue) => {
    const stage = pipelineStages.find(s => s.value === stageValue);
    return { name: stage?.name || stageValue?.replace(/_/g, " "), color: stage?.color || "#6B7280" };
  };

  const getCurrentCategory = () => {
    const stage = pipelineStages.find(s => s.value === formData.stage);
    return stage ? pipelineCategories.find(c => c.value === stage.category) : null;
  };

  // Event handlers
  const handleEventTypeChange = (typeId) => {
    const et = eventTypes.find(t => t.id === typeId);
    if (!et) { setEventForm(f => ({ ...f, event_type_id: "" })); return; }
    // When picking an event type, auto-fill title + start time + compute end based on duration_hours
    const startTime = et.default_start_time || "08:00";
    const [h, m] = startTime.split(":").map(Number);
    const totalMin = (isNaN(h) ? 8 : h) * 60 + (isNaN(m) ? 0 : m) + Math.round((et.duration_hours || 1) * 60);
    const endH = Math.min(23, Math.floor(totalMin / 60));
    const endM = totalMin % 60;
    const endTime = `${String(endH).padStart(2, "0")}:${String(endM).padStart(2, "0")}`;
    setEventForm(f => ({
      ...f,
      event_type_id: typeId,
      title: et.name || f.title,
      start_time: startTime,
      end_time: endTime,
    }));
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    try {
      if (editingEventId) {
        // Edit existing — PUT preserves the DB id but refreshes all fields.
        await axios.put(`${API_URL}/schedule/${editingEventId}`, {
          ...eventForm,
          project_id: id,
          contact_id: project?.contact_id || "",
        });
        toast.success("Event updated!");
      } else {
        await axios.post(`${API_URL}/schedule`, { ...eventForm, project_id: id, contact_id: project?.contact_id || "" });
        toast.success("Event created!");
      }
      setShowEventModal(false);
      setEditingEventId(null);
      loadEvents();
      // Refetch the project after the backend's on_appointment_booked
      // trigger has run — this pulls the freshly auto-moved stage
      // (e.g. Appointment Scheduled) into our local formData so the next
      // autosave doesn't stomp the change with a stale copy. Merge into
      // formData too so the Stage dropdown reflects the update immediately.
      try {
        const r = await axios.get(`${API_URL}/projects/${id}`);
        setProject(r.data);
        setFormData(prev => ({ ...prev, stage: r.data.stage }));
        fetchActivities("project", id).then(setActivities);
      } catch {}
      setEventForm({ title: "", event_type_id: "", start_date: "", start_time: "08:00", end_time: "17:00", notes: "", assigned_to: user?.id || "" });
    } catch { toast.error(editingEventId ? "Failed to update event" : "Failed to create event"); }
  };

  const openEditEvent = (ev) => {
    setEditingEventId(ev.id);
    setEventForm({
      title: ev.title || "",
      event_type_id: ev.event_type_id || "",
      start_date: ev.start_date || "",
      start_time: ev.start_time || "08:00",
      end_time: ev.end_time || "17:00",
      notes: ev.notes || "",
      assigned_to: ev.assigned_to || "",
    });
    setShowEventModal(true);
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm("Delete this event?")) return;
    try { await axios.delete(`${API_URL}/schedule/${eventId}`); toast.success("Moved to trash"); loadEvents(); }
    catch { toast.error("Failed to delete event"); }
  };

  // Task handlers
  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      await createTask({ ...taskForm, project_id: id });
      toast.success("Task created!"); setShowTaskModal(false);
      setTaskForm({ title: "", priority: "medium", due_date: "", notes: "" });
    } catch { toast.error("Failed to create task"); }
  };

  // Invoice handlers
  const handleCreateInvoice = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/invoices`, {
        ...invoiceForm,
        project_id: id,
        amount: parseFloat(invoiceForm.amount) || 0,
        discount: parseFloat(invoiceForm.discount) || 0,
        deposit_percent: invoiceForm.deposit_percent,
        payments_applied: parseFloat(invoiceForm.payments_applied) || 0,
      });
      toast.success("Invoice created!"); setShowInvoiceModal(false); loadBudget();
      setInvoiceForm({ invoice_type: "deposit", deposit_mode: "percent", title: "", description: "", amount: "", due_date: "", quote_id: "", line_items: [], discount: 0, deposit_percent: null, payments_applied: 0 });
    } catch { toast.error("Failed to create invoice"); }
  };

  const handleInvoiceTypeChange = async (type) => {
    setInvoiceForm(prev => ({ ...prev, invoice_type: type }));
    // Auto-calculate from signed quote
    const quoteId = invoiceForm.quote_id || "";
    try {
      const res = await axios.get(`${API_URL}/invoice-calc/${id}?invoice_type=${type}&quote_id=${quoteId}`);
      const d = res.data;
      if (!d.error) {
        setInvoiceForm(prev => ({
          ...prev,
          invoice_type: type,
          amount: d.amount || 0,
          description: d.description || "",
          quote_id: d.quote_id || prev.quote_id,
          line_items: d.line_items || [],
          deposit_percent: d.deposit_percent || null,
          payments_applied: d.payments_applied || 0,
        }));
      }
    } catch {}
  };

  const handleUpdateInvoice = async (invoiceId) => {
    try {
      await axios.put(`${API_URL}/invoices/${invoiceId}`, {
        title: editInvoiceForm.title,
        description: editInvoiceForm.description,
        amount: parseFloat(editInvoiceForm.amount) || 0,
        due_date: editInvoiceForm.due_date,
        status: editInvoiceForm.status,
        line_items: editInvoiceForm.line_items,
        discount: parseFloat(editInvoiceForm.discount) || 0,
        discount_label: editInvoiceForm.discount_label || "",
      });
      toast.success("Invoice updated!"); setEditingInvoiceId(null); loadBudget();
    } catch { toast.error("Failed to update invoice"); }
  };

  const startEditInvoice = (inv) => {
    setEditingInvoiceId(inv.id);
    const items = inv.line_items && inv.line_items.length > 0
      ? inv.line_items
      : [{ id: `li-${Date.now()}`, description: inv.description || "", quantity: 1, unit_price: inv.amount || 0, amount: inv.amount || 0 }];
    setEditInvoiceForm({
      title: inv.title || "",
      description: inv.description || "",
      amount: inv.amount || 0,
      due_date: inv.due_date ? inv.due_date.split("T")[0] : "",
      status: inv.status || "sent",
      quote_id: inv.quote_id || "",
      line_items: items,
      discount: inv.discount || 0,
      discount_label: inv.discount_label || "",
    });
  };

  const _recomputeInvoiceTotal = (items, discount) => {
    const subtotal = (items || []).reduce((s, it) => s + (parseFloat(it.amount) || 0), 0);
    const disc = parseFloat(discount) || 0;
    return Math.max(0, subtotal - disc);
  };

  const updateLineItem = (idx, field, value) => {
    setEditInvoiceForm(prev => {
      const items = [...prev.line_items];
      items[idx] = { ...items[idx], [field]: value };
      if (field === "quantity" || field === "unit_price") {
        items[idx].amount = parseFloat(items[idx].quantity || 0) * parseFloat(items[idx].unit_price || 0);
      }
      return { ...prev, line_items: items, amount: _recomputeInvoiceTotal(items, prev.discount) };
    });
  };
  const round2 = (n) => Math.round(n * 100) / 100;

  const addLineItem = () => {
    setEditInvoiceForm(prev => {
      const items = [...prev.line_items, { id: `li-${Date.now()}`, description: "", quantity: 1, unit_price: 0, amount: 0 }];
      return { ...prev, line_items: items, amount: _recomputeInvoiceTotal(items, prev.discount) };
    });
  };

  const removeLineItem = (idx) => {
    setEditInvoiceForm(prev => {
      const items = prev.line_items.filter((_, i) => i !== idx);
      return { ...prev, line_items: items, amount: _recomputeInvoiceTotal(items, prev.discount) };
    });
  };

  const handlePreviewInvoice = async (invoiceId) => {
    try {
      const { data } = await axios.get(`${API_URL}/invoices/${invoiceId}`);
      setInvoicePreview(data);
    } catch { toast.error("Failed to load invoice preview"); }
  };

  const handleSendInvoice = (invoiceId, invoiceNumber = "") => {
    setSendInvoiceDialog({ open: true, invoiceId, invoiceNumber });
  };

  // Payment handlers
  const handleCreatePayment = async (e) => {
    e.preventDefault();
    const { silent, ...body } = paymentForm;
    try {
      await axios.post(
        `${API_URL}/payments${silent ? "?silent=true" : ""}`,
        { ...body, project_id: id, amount: parseFloat(paymentForm.amount) || 0 },
      );
      toast.success(silent ? "Correction recorded — nothing sent to the customer" : "Payment recorded!");
      setShowPaymentModal(false); loadBudget();
      // Reset with today's date pre-filled so the next payment doesn't
      // silently fall back to "assume today" on the server.
      setPaymentForm({ amount: "", method: "check", reference: "", invoice_id: "", date: new Date().toISOString().slice(0, 10), silent: false });
    } catch { toast.error("Failed to record payment"); }
  };

  // Cost handlers
  const handleCreateCost = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/project-costs`, { ...costForm, project_id: id, amount: parseFloat(costForm.amount) || 0 });
      toast.success("Cost added!"); setShowCostModal(false); loadBudget();
      setCostForm({ name: "", amount: "", cost_type: "material", notes: "" });
    } catch { toast.error("Failed to add cost"); }
  };

  const handleUpdateCost = async () => {
    if (!editingCostId) return;
    try {
      await axios.put(`${API_URL}/project-costs/${editingCostId}`, {
        ...editCostForm,
        project_id: id,
        amount: parseFloat(editCostForm.amount) || 0,
      });
      toast.success("Cost updated");
      setEditingCostId(null);
      loadBudget();
    } catch { toast.error("Failed to update cost"); }
  };

  const startEditCost = (cost) => {
    setEditingCostId(cost.id);
    setEditCostForm({
      name: cost.name || "",
      amount: cost.amount ?? "",
      cost_type: cost.cost_type || "material",
      notes: cost.notes || "",
    });
  };

  const handleMarkInvoicePaid = async (inv) => {
    if (inv.status === "paid") return;
    // Two-step confirm keeps this lightweight vs pulling in the full
    // dialog component. First: card vs cash. Second: final confirm.
    const orig = parseFloat(inv.amount) || 0;
    const cashAmt = Math.round(orig * 0.98 * 100) / 100;
    const wantsCash = window.confirm(
      `Is ${inv.invoice_number || "this invoice"} being paid in CASH?\n\n` +
      `• OK = Cash ($${cashAmt.toLocaleString(undefined,{minimumFractionDigits:2})} — 2% discount)\n` +
      `• Cancel = Card / Check ($${orig.toLocaleString(undefined,{minimumFractionDigits:2})} — full amount)`
    );
    const summary = wantsCash
      ? `Mark paid at CASH price $${cashAmt.toLocaleString(undefined,{minimumFractionDigits:2})}?`
      : `Mark paid at full price $${orig.toLocaleString(undefined,{minimumFractionDigits:2})}?`;
    if (!window.confirm(summary)) return;
    // Third question: is this a correction for the books, or a real payment
    // the customer should be receipted for? OK = correction (silent).
    const silent = window.confirm(
      "Send the customer a receipt?\n\n" +
      "• Cancel = YES, send the receipt and run the usual pipeline automation\n" +
      "• OK = NO — record correction only, no customer notification, no stage change"
    );
    try {
      await axios.post(`${API_URL}/invoices/${inv.id}/mark-paid?cash=${wantsCash ? "true" : "false"}&silent=${silent ? "true" : "false"}`);
      toast.success(
        silent
          ? "Correction recorded — nothing sent to the customer"
          : (wantsCash ? "Invoice marked paid (cash)" : "Invoice marked paid")
      );
      loadBudget();
      loadProject();
    } catch (err) {
      console.error("[Mark Paid] failed:", err);
      toast.error(err?.response?.data?.detail || "Failed to mark invoice paid");
    }
  };

  const handleRegenerateInvoice = async (inv) => {
    if (!window.confirm(`Regenerate invoice ${inv.invoice_number || ""} from the current signed quote?\n\nAmount + line items will update to match the latest quote total (minus any other invoices already on this project). Use this after a quote total changes (change orders, healed totals, edits).`)) return;
    try {
      const res = await axios.post(`${API_URL}/invoices/${inv.id}/regenerate`);
      const prev = res.data?.previous_amount ?? inv.amount;
      const next = res.data?.amount ?? prev;
      toast.success(`Invoice updated: $${Number(prev).toFixed(2)} → $${Number(next).toFixed(2)}`);
      loadBudget();
      loadProject();
    } catch (err) {
      console.error("[Regenerate Invoice] failed:", err);
      toast.error(err?.response?.data?.detail || "Failed to regenerate invoice");
    }
  };

  // Manual rescue for the pipeline auto-move. Fires the same check that runs
  // after every payment; useful when a payment landed but the project didn't
  // advance because of a lingering unpaid invoice that has since been fixed.
  const handleRefreshBillingStage = async () => {
    try {
      const res = await axios.post(`${API_URL}/projects/${id}/refresh-billing-stage`);
      const d = res.data || {};
      if (d.advanced) {
        toast.success(`Moved to ${d.to_stage_name || d.to_stage}`);
        loadProject();
        loadBudget();
      } else if (d.reason === "one or more invoices still unpaid") {
        const list = (d.unpaid_invoices || []).map(u => `${u.invoice_number || u.id}: $${(u.amount - u.paid_amount).toFixed(2)} still due`).join("\n");
        toast.error(`Can't advance — unpaid invoices:\n${list}`, { duration: 8000 });
      } else if (d.reason === "already at or past Final Paid") {
        toast.info(`Already at ${d.current_stage_name || d.current_stage}`);
      } else {
        toast.info(`No change: ${d.reason || "not eligible"}`);
      }
    } catch (err) {
      console.error("[Refresh Billing Stage] failed:", err);
      toast.error(err?.response?.data?.detail || "Failed to refresh stage");
    }
  };

  const handleDeleteItem = async (type, itemId) => {
    if (!window.confirm(`Delete this ${type}?`)) return;
    try { await axios.delete(`${API_URL}/${type}/${itemId}`); toast.success("Deleted"); loadBudget(); }
    catch { toast.error("Failed to delete"); }
  };

  const [embeddedReportId, setEmbeddedReportId] = useState(null);

  const handleCreateRoofReport = async () => {
    try {
      const res = await axios.post(`${API_URL}/roof-reports`, {
        name: `${contact?.name || "Project"} - Roof Report`,
        project_id: id,
        structures: [{ name: "Structure 1", waste_factor: 0, total_area: 0, rakes: 0, eaves: 0, ridges: 0, valleys: 0, hips: 0, boots: 0, transitions: 0, osb: 0, sealant: 0, spray: 0, flashing: 0, drip_edge: 0, delivery: 0, labor_low: 0, labor_mid: 0, labor_high: 0 }],
        date: new Date().toISOString().split("T")[0],
      });
      toast.success("Roof report created!");
      // Stay inside the project — open the new report inline instead of
      // routing away to /measurements. The Measurements tab still lists it.
      await loadRoofReports();
      setEmbeddedReportId(res.data.id);
    } catch { toast.error("Failed to create report"); }
  };

  const [showAttachReport, setShowAttachReport] = useState(false);
  const [allReports, setAllReports] = useState([]);
  const loadAllReports = async () => {
    try {
      const res = await axios.get(`${API_URL}/roof-reports`);
      // Filter out reports already linked to this project
      const linkedIds = new Set(roofReports.map(r => r.id));
      setAllReports(res.data.filter(r => !linkedIds.has(r.id)));
    } catch { /* */ }
  };
  const handleAttachReport = async (reportId) => {
    try {
      await axios.patch(`${API_URL}/roof-reports/${reportId}`, { project_id: id });
      toast.success("Report linked to project!");
      setShowAttachReport(false);
      loadRoofReports();
    } catch { toast.error("Failed to link report"); }
  };

  const ROOF_COMPONENTS = [
    { area: "Shingles / Roofing Material", condition: "good", notes: "" },
    { area: "Ridge Caps", condition: "good", notes: "" },
    { area: "Flashing", condition: "good", notes: "" },
    { area: "Valleys", condition: "good", notes: "" },
    { area: "Gutters & Downspouts", condition: "good", notes: "" },
    { area: "Fascia & Drip Edge", condition: "good", notes: "" },
    { area: "Soffits & Ventilation", condition: "good", notes: "" },
    { area: "Pipe Boots & Penetrations", condition: "good", notes: "" },
    { area: "Decking / Substrate", condition: "good", notes: "" },
    { area: "Skylights / Chimneys", condition: "na", notes: "" },
  ];

  const handleCreateInspection = () => {
    navigate(`/inspections?create=true&project_id=${id}`);
  };

  if (loading) return <DetailPageSkeleton />;

  const stageInfo = getStageInfo(formData.stage);
  const category = getCurrentCategory();

  const projectTabs = [
    { value: "review", label: "Review", icon: Zap },
    { value: "activity", label: "Activity", icon: Clock },
    { value: "emails", label: "Emails", icon: Mail },
    { value: "workflows", label: "Workflows", icon: Workflow },
    { value: "events", label: "Events", icon: CalendarDays },
    { value: "files", label: "Files", icon: Camera },
    { value: "tasks", label: "Tasks", icon: CheckSquare },
    { value: "inspection", label: "Inspection", icon: ClipboardCheck },
    { value: "measure", label: "Measure", icon: Ruler },
    { value: "quote", label: "Quote", icon: Receipt },
    { value: "order", label: "Order", icon: ShoppingCart },
    { value: "jobs", label: "Jobs", icon: Briefcase },
    { value: "billing", label: "Billing", icon: CreditCard },
    { value: "budget", label: "Budget", icon: PiggyBank },
  ];

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-4 sm:px-6 py-4 sticky top-0 z-30">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Button variant="ghost" size="icon" className="h-10 w-10 flex-shrink-0" onClick={() => goBack("/projects")} data-testid="back-to-projects-btn"><ArrowLeft className="w-5 h-5" /></Button>
            {project?.cover_photo_url && (
              <button
                type="button"
                onClick={() => setActiveTab("files")}
                className="h-11 w-11 sm:h-12 sm:w-12 rounded-lg overflow-hidden border-2 border-amber-400 flex-shrink-0 hover:ring-2 hover:ring-amber-300 transition-shadow"
                title="Cover photo — used on Job Complete + Review Request MMS. Click to manage."
                data-testid="project-header-cover-thumb"
              >
                <img src={project.cover_photo_url} alt="Project cover" className="w-full h-full object-cover" />
              </button>
            )}
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold text-slate-800 truncate" data-testid="page-header">{contact?.name || "Project Details"}</h1>
              {formData.project_address && <p className="text-sm text-slate-500 flex items-center gap-1 mt-0.5 truncate"><MapPin className="w-3 h-3 flex-shrink-0" />{formData.project_address}</p>}
              <div className="mt-0.5"><LastEditedLabel updatedAt={project?.updated_at} /></div>
            </div>
          </div>
          {/* Actions: stage / save / delete. Wraps to a second row on narrow
              screens so the Delete button never overflows offscreen on mobile. */}
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 justify-start sm:justify-end flex-shrink-0">
            {category && <Badge className="text-xs h-7 hidden sm:inline-flex" style={{ backgroundColor: `${category.color}20`, color: category.color }}>{category.name}</Badge>}
            <Select value={formData.stage || ""} onValueChange={async (v) => {
              // Special-case moves into "Paid & Closed" so the admin can
              // opt out of review-request nagging or silence the entire
              // referral nurture flow before the workflow kicks off.
              if (v === "billing_paid_closed" && formData.stage !== "billing_paid_closed") {
                setPaidClosedDialog({
                  open: true,
                  pendingStage: v,
                  skipReview: !!formData.skip_review_request,
                  disableOutreach: !!formData.disable_customer_outreach,
                });
                return;
              }
              await handleStageChange(v);
              fetchActivities("project", id).then(setActivities);
            }} data-testid="header-stage-select">
              <SelectTrigger className="h-8 border-0 gap-1.5 px-3 rounded-full font-medium text-sm w-auto" style={{ backgroundColor: `${stageInfo.color}20`, color: stageInfo.color }} data-testid="project-stage-badge">
                <SelectValue>{stageInfo.name}</SelectValue>
              </SelectTrigger>
              <SelectContent className="max-h-80">
                {pipelineCategories.map(cat => {
                  const catStages = pipelineStages.filter(s => s.category === cat.value);
                  if (!catStages.length) return null;
                  return (
                    <div key={cat.value}>
                      <div className="px-2 py-1.5 text-[10px] uppercase font-bold tracking-wider" style={{ color: cat.color }}>{cat.name}</div>
                      {catStages.map(s => (
                        <SelectItem key={s.value} value={s.value} className="text-sm pl-4">
                          <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: s.color }} />{s.name}</span>
                        </SelectItem>
                      ))}
                    </div>
                  );
                })}
              </SelectContent>
            </Select>
            <AutosaveStatus status={autosaveStatus} savedAt={savedAt} />
            <Button className="bg-[#0F4C81] hover:bg-[#0D4270] h-9 px-3 sm:h-10 sm:px-5" onClick={handleSave} disabled={saving} data-testid="save-project-btn">
              {saving ? <Loader2 className="w-4 h-4 sm:mr-2 animate-spin" /> : <Save className="w-4 h-4 sm:mr-2" />}<span className="hidden sm:inline">Save</span>
            </Button>
            <Button
              variant="outline"
              className="h-9 px-3 sm:h-10 border-red-300 text-red-600 hover:bg-red-50"
              data-testid="delete-project-btn"
              onClick={async () => {
                if (!window.confirm(`Delete this project? It will be moved to Trash and can be restored from there.`)) return;
                try {
                  await deleteProject(id);
                  toast.success("Project moved to Trash");
                  goBack("/projects");
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Failed to delete project");
                }
              }}
            >
              <Trash2 className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Delete</span>
            </Button>
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-6 pb-28 sm:pb-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar */}
          <div className="space-y-4">
            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-sm flex items-center gap-2"><User className="w-4 h-4" />Contact</CardTitle></CardHeader>
              <CardContent>
                {contact ? (
                  <div className="space-y-2">
                    <div className="p-3 bg-slate-50 rounded-lg" data-testid="project-contact-card">
                      <p className="font-medium text-slate-800 text-sm">{contact.name}</p>
                      {contact.phone && (
                        <p className="text-xs text-slate-500 mt-1">
                          <a href={`tel:${contact.phone}`} className="hover:text-[#0F4C81] hover:underline" onClick={(e) => e.stopPropagation()}>{contact.phone}</a>
                        </p>
                      )}
                      {contact.email && (
                        <p className="text-xs text-slate-500">
                          <a href={`mailto:${contact.email}`} className="hover:text-[#0F4C81] hover:underline" onClick={(e) => e.stopPropagation()}>{contact.email}</a>
                        </p>
                      )}
                      <button
                        type="button"
                        className="text-[11px] text-[#0F4C81] hover:underline mt-2"
                        onClick={() => navigate(`/contacts/${contact.id}?fromProject=${id}`)}
                        data-testid="project-contact-link"
                      >
                        Open full contact →
                      </button>
                    </div>
                    <SmsConsentToggle contact={contact} compact onUpdated={() => fetchContacts()} />
                    <select
                      className="w-full text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#00A3E0]/30 text-slate-500"
                      value={project?.contact_id || ""}
                      onChange={e => {
                        const newData = { ...formData, contact_id: e.target.value, estimated_value: parseFloat(formData.estimated_value) || 0 };
                        setFormData(prev => ({ ...prev, contact_id: e.target.value }));
                        updateProject(id, newData).then(u => { setProject(u); toast.success(e.target.value ? "Contact updated" : "Contact unlinked"); }).catch(() => toast.error("Failed"));
                      }}
                      data-testid="change-contact-select"
                    >
                      <option value="">Unlink contact</option>
                      {contacts.map(c => <option key={c.id} value={c.id}>{c.name}{c.phone ? ` — ${c.phone}` : ""}</option>)}
                    </select>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-slate-400 text-sm">No contact linked</p>
                    <select
                      className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-[#00A3E0]/30"
                      value=""
                      onChange={e => {
                        if (!e.target.value) return;
                        const newData = { ...formData, contact_id: e.target.value, estimated_value: parseFloat(formData.estimated_value) || 0 };
                        setFormData(prev => ({ ...prev, contact_id: e.target.value }));
                        updateProject(id, newData).then(u => { setProject(u); toast.success("Contact linked"); }).catch(() => toast.error("Failed"));
                      }}
                      data-testid="link-contact-select"
                    >
                      <option value="">Select a contact...</option>
                      {contacts.map(c => <option key={c.id} value={c.id}>{c.name}{c.phone ? ` — ${c.phone}` : ""}</option>)}
                    </select>
                  </div>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-sm">Quick Info</CardTitle></CardHeader>
              <CardContent className="space-y-2.5 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">Created</span><span className="font-medium text-xs">{project?.created_at ? new Date(project.created_at).toLocaleDateString() : "N/A"}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Updated</span><span className="font-medium text-xs">{project?.updated_at ? new Date(project.updated_at).toLocaleDateString() : "N/A"}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Classification</span><Badge variant="outline" className="capitalize text-xs">{project?.classification || "N/A"}</Badge></div>
                {project?.estimated_value > 0 && <div className="flex justify-between"><span className="text-slate-500">Value</span><span className="font-medium text-emerald-600 text-xs flex items-center"><DollarSign className="w-3 h-3" />{project.estimated_value.toLocaleString()}</span></div>}
                {project?.referral_name && <div className="flex justify-between"><span className="text-slate-500">Referral</span><span className="font-medium text-xs flex items-center gap-1"><UserPlus className="w-3 h-3 text-[#00A3E0]" />{project.referral_name}</span></div>}
              </CardContent>
            </Card>
          </div>

          {/* Main Tabs */}
          <div className="lg:col-span-3">
            <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); if (v === "billing" || v === "budget" || v === "review") loadBudget(); }} className="w-full" data-testid="project-tabs">
              <TabsList className="w-full h-auto flex flex-nowrap overflow-x-auto md:flex-wrap justify-start bg-white border border-slate-200 rounded-lg p-1 gap-0.5 scrollbar-hide" data-testid="project-tabs-list">
                {projectTabs.map(tab => (
                  <TabsTrigger key={tab.value} value={tab.value} className="flex-shrink-0 text-xs sm:text-sm px-2.5 py-1.5 data-[state=active]:bg-[#0F4C81] data-[state=active]:text-white rounded-md relative" data-testid={`project-tab-${tab.value}`}>
                    <tab.icon className="w-3.5 h-3.5 mr-1.5 hidden sm:inline-block" />{tab.label}
                    {tab.value === "emails" && emailBounceCount > 0 && (
                      <span
                        className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold leading-none"
                        data-testid="email-bounce-badge"
                        title={`${emailBounceCount} bounced/failed email${emailBounceCount === 1 ? "" : "s"}`}
                      >
                        {emailBounceCount > 99 ? "99+" : emailBounceCount}
                      </span>
                    )}
                  </TabsTrigger>
                ))}
              </TabsList>

              {/* REVIEW TAB - Quick 60-second overview */}
              <TabsContent value="review" className="mt-4" data-testid="project-tab-content-review">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                  {/* Contact & Project Info */}
                  <Card className="border-l-4 border-l-[#0F4C81]">
                    <CardContent className="pt-5 pb-4">
                      <p className="text-[10px] uppercase font-bold text-[#0F4C81] tracking-wider mb-3">Contact & Project</p>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2.5">
                          <User className="w-4 h-4 text-slate-400 flex-shrink-0" />
                          <span className="text-base font-semibold text-slate-800">{contact?.name || "No contact"}</span>
                        </div>
                        {contact?.phone && <div className="flex items-center gap-2.5"><Phone className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" /><a href={`tel:${contact.phone}`} className="text-sm text-[#00A3E0] hover:underline">{contact.phone}</a></div>}
                        {contact?.email && <div className="flex items-center gap-2.5"><Mail className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" /><a href={`mailto:${contact.email}`} className="text-sm text-[#00A3E0] hover:underline break-all">{contact.email}</a></div>}
                        {formData.project_address && <div className="flex items-center gap-2.5"><MapPin className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" /><span className="text-sm text-slate-600">{formData.project_address}</span></div>}
                        {/* One-tap Call / Text / Email — thumb-zone buttons for the phone.
                            Uses native tel:, sms:, and mailto: schemes so the phone's
                            default apps handle the actual action. */}
                        {/* Quick-actions: Call still uses tel: so the
                            phone dialer opens (calls go through YOUR
                            number). Text now routes to the CRM Inbox
                            so messages go through the Twilio business
                            number, not the user's personal SMS app. */}
                        {(contact?.phone || contact?.email) && (
                          <div className="grid grid-cols-3 gap-2 pt-2" data-testid="project-contact-quick-actions">
                            <a
                              href={contact?.phone ? `tel:${contact.phone}` : undefined}
                              onClick={(e) => { if (!contact?.phone) e.preventDefault(); }}
                              className={`flex items-center justify-center h-11 rounded-lg text-sm font-medium transition-colors ${contact?.phone ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100 active:bg-emerald-200" : "bg-slate-50 text-slate-300 pointer-events-none"}`}
                              data-testid="project-quick-call"
                            >
                              <Phone className="w-4 h-4 mr-1.5" />Call
                            </a>
                            <button
                              type="button"
                              disabled={!contact?.phone}
                              onClick={() => {
                                if (!contact?.phone) return;
                                // Deep-link into the CRM Inbox with this
                                // contact pre-selected. The Inbox reads
                                // ?phone=... and opens the conversation
                                // (or creates a new one) tied to the
                                // Twilio business number.
                                navigate(`/inbox?phone=${encodeURIComponent(contact.phone)}`);
                              }}
                              className={`flex items-center justify-center h-11 rounded-lg text-sm font-medium transition-colors ${contact?.phone ? "bg-sky-50 text-sky-700 hover:bg-sky-100 active:bg-sky-200" : "bg-slate-50 text-slate-300 cursor-not-allowed"}`}
                              data-testid="project-quick-text"
                            >
                              <MessageSquare className="w-4 h-4 mr-1.5" />Text
                            </button>
                            <a
                              href={contact?.email ? `mailto:${contact.email}` : undefined}
                              onClick={(e) => { if (!contact?.email) e.preventDefault(); }}
                              className={`flex items-center justify-center h-11 rounded-lg text-sm font-medium transition-colors ${contact?.email ? "bg-indigo-50 text-indigo-700 hover:bg-indigo-100 active:bg-indigo-200" : "bg-slate-50 text-slate-300 pointer-events-none"}`}
                              data-testid="project-quick-email"
                            >
                              <Mail className="w-4 h-4 mr-1.5" />Email
                            </a>
                          </div>
                        )}
                        <div className="flex flex-wrap gap-2 pt-1">
                          {category && <Badge className="text-[10px]" style={{ backgroundColor: `${category.color}20`, color: category.color }}>{category.name}</Badge>}
                          <Badge className="text-[10px]" style={{ backgroundColor: `${stageInfo.color}20`, color: stageInfo.color }}>{stageInfo.name}</Badge>
                          {project?.classification && <Badge variant="outline" className="text-[10px] capitalize">{project.classification}</Badge>}
                          {project?.lead_source && <Badge variant="outline" className="text-[10px] capitalize">{project.lead_source}</Badge>}
                        </div>
                        {project?.referral_name && <div className="flex items-center gap-2.5 pt-0.5"><UserPlus className="w-3.5 h-3.5 text-[#00A3E0] flex-shrink-0" /><span className="text-xs text-slate-500">Referred by <span className="font-medium text-slate-700">{project.referral_name}</span></span></div>}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Billing Snapshot */}
                  <Card className="border-l-4 border-l-emerald-500">
                    <CardContent className="pt-5 pb-4">
                      <p className="text-[10px] uppercase font-bold text-emerald-600 tracking-wider mb-3">Billing Snapshot</p>
                      {budget ? (
                        <div className="space-y-3">
                          <div className="grid grid-cols-2 gap-2">
                            <div className="bg-slate-50 rounded-md p-2.5"><p className="text-[10px] text-slate-400 uppercase font-semibold">Contract</p><p className="text-base font-bold text-slate-800">{fmtCurrency(budget.contract_value)}</p></div>
                            <div className="bg-slate-50 rounded-md p-2.5"><p className="text-[10px] text-slate-400 uppercase font-semibold">Paid</p><p className="text-base font-bold text-emerald-600">{fmtCurrency(budget.total_payments)}</p></div>
                            <div className="bg-slate-50 rounded-md p-2.5"><p className="text-[10px] text-slate-400 uppercase font-semibold">Invoiced</p><p className="text-base font-bold text-blue-600">{fmtCurrency(budget.total_invoiced)}</p></div>
                            <div className="bg-slate-50 rounded-md p-2.5"><p className="text-[10px] text-slate-400 uppercase font-semibold">Outstanding</p><p className={`text-base font-bold ${budget.outstanding_balance > 0 ? "text-red-600" : "text-slate-400"}`}>{fmtCurrency(budget.outstanding_balance)}</p></div>
                          </div>
                          {/* Last billing actions */}
                          {(budget.invoices?.length > 0 || budget.payments?.length > 0) && (
                            <div className="pt-1 border-t border-slate-100">
                              <p className="text-[10px] text-slate-400 uppercase font-semibold mb-1.5">Last Actions</p>
                              <div className="space-y-1.5">
                                {budget.invoices?.slice(-1).map(inv => (
                                  <div key={inv.id} className="flex items-center justify-between text-xs">
                                    <span className="text-slate-600"><FileText className="w-3 h-3 inline mr-1 text-blue-500" />Invoice {inv.invoice_number}</span>
                                    <div className="flex items-center gap-2">
                                      <span className="font-semibold">{fmtCurrency(inv.amount)}</span>
                                      <Badge variant={inv.status === "paid" ? "default" : "outline"} className="text-[9px] capitalize h-4 px-1.5">{inv.status}</Badge>
                                    </div>
                                  </div>
                                ))}
                                {budget.payments?.slice(-1).map(p => (
                                  <div key={p.id} className="flex items-center justify-between text-xs">
                                    <span className="text-slate-600"><DollarSign className="w-3 h-3 inline mr-1 text-emerald-500" />Payment via {p.method}</span>
                                    <span className="font-semibold text-emerald-600">{fmtCurrency(p.amount)}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 gap-2">
                          <div className="bg-slate-50 rounded-md p-2.5"><p className="text-[10px] text-slate-400 uppercase font-semibold">Est. Value</p><p className="text-base font-bold text-slate-800">{fmtCurrency(project?.estimated_value)}</p></div>
                          <div className="bg-slate-50 rounded-md p-2.5 text-center"><p className="text-xs text-slate-400 mt-2">No billing data yet</p></div>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Quotes / Options */}
                  <Card className="border-l-4 border-l-[#00A3E0]">
                    <CardContent className="pt-5 pb-4">
                      <p className="text-[10px] uppercase font-bold text-[#00A3E0] tracking-wider mb-3">Quotes & Options</p>
                      {projectQuotes.length > 0 ? (
                        <div className="space-y-2">
                          {projectQuotes.map(q => {
                            const tpl = q.template_id ? (quoteTemplates || []).find(t => t.id === q.template_id) : null;
                            const tplName = tpl?.name || "Quote";
                            return (
                              <div key={q.id} className="bg-slate-50 rounded-md p-3 flex items-center justify-between cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => setActiveTab("quote")} data-testid={`review-quote-${q.id}`}>
                                <div>
                                  <p className="text-sm font-semibold text-slate-700">
                                    <span>{q.quote_number || "Draft"}</span>
                                    <span className="text-slate-400"> · </span>
                                    <span className="text-[#0F4C81]">{tplName}</span>
                                  </p>
                                  <p className="text-[10px] text-slate-400 mt-0.5">{new Date(q.created_at).toLocaleDateString()}</p>
                                </div>
                                <div className="text-right">
                                  <Badge variant={q.status === "approved" ? "default" : q.status === "sent" ? "secondary" : "outline"} className={`text-[9px] capitalize mt-0.5 ${isQuoteExpired(q) ? "border-amber-300 text-amber-700" : ""}`}>{isQuoteExpired(q) ? "expired" : q.status}</Badge>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : <p className="text-xs text-slate-400 text-center py-4">No quotes created</p>}
                    </CardContent>
                  </Card>

                  {/* Recent Activity */}
                  <Card className="border-l-4 border-l-amber-500">
                    <CardContent className="pt-5 pb-4">
                      <p className="text-[10px] uppercase font-bold text-amber-600 tracking-wider mb-3">Recent Activity</p>
                      {activities.length > 0 ? (
                        <div className="space-y-2.5">
                          {activities.slice(0, 3).map(act => {
                            const relTime = (() => {
                              const diff = Math.floor((Date.now() - new Date(act.created_at)) / 1000);
                              if (diff < 60) return "just now";
                              if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
                              if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
                              if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
                              return new Date(act.created_at).toLocaleDateString();
                            })();
                            const colorMap = { created: "bg-emerald-500", stage_changed: "bg-blue-500", photo_uploaded: "bg-purple-500", email_sent: "bg-amber-500", customer_signed: "bg-emerald-500" };
                            return (
                              <div key={act.id} className="flex gap-2.5">
                                <div className={`w-2 h-2 rounded-full ${colorMap[act.action] || "bg-slate-400"} flex-shrink-0 mt-1.5`} />
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs text-slate-700 leading-snug truncate">{act.details}</p>
                                  <p className="text-[10px] text-slate-400 mt-0.5">{act.user_name || "System"} &middot; {relTime}</p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : <p className="text-xs text-slate-400 text-center py-4">No activity yet</p>}
                    </CardContent>
                  </Card>

                </div>
              </TabsContent>

              {/* Activity */}
              <TabsContent value="activity" className="mt-4"><Card><CardHeader className="pb-3"><CardTitle className="text-base">Activity Timeline</CardTitle></CardHeader><CardContent><ActivityTimeline activities={activities} /></CardContent></Card></TabsContent>

              {/* Emails */}
              <TabsContent value="emails" className="mt-4" data-testid="project-tab-content-emails"><ProjectEmails projectId={id} /></TabsContent>

              {/* Workflows */}
              <TabsContent value="workflows" className="mt-4" data-testid="project-tab-content-workflows"><ProjectAutomations projectId={id} /></TabsContent>

              {/* Events */}
              <TabsContent value="events" className="mt-4" data-testid="project-tab-content-events">
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">Events</CardTitle>
                      <Button size="sm" className="bg-[#0F4C81] hover:bg-[#0D4270] h-9" onClick={() => { setEventForm(prev => ({ ...prev, assigned_to: prev.assigned_to || user?.id || "" })); setShowEventModal(true); }} data-testid="create-project-event-btn"><Plus className="w-4 h-4 mr-1" />New Event</Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {events.length > 0 ? (
                      <div className="space-y-2">
                        {events.map(ev => (
                          <div key={ev.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg group hover:bg-slate-100 transition-colors">
                            <button
                              type="button"
                              onClick={() => openEditEvent(ev)}
                              className="text-left flex-1 min-w-0"
                              data-testid={`edit-event-${ev.id}`}
                            >
                              <p className="text-sm font-medium text-slate-700">{ev.title}</p>
                              <p className="text-xs text-slate-500">{ev.start_date} {ev.start_time && `at ${fmtTime12(ev.start_time)}`}{ev.end_time && ` - ${fmtTime12(ev.end_time)}`}</p>
                              {ev.assigned_to && (() => {
                                const u = teamUsers.find(t => t.id === ev.assigned_to);
                                return u ? <p className="text-xs text-[#0F4C81] mt-0.5">Assigned to: {u.name || u.email}</p> : null;
                              })()}
                              {ev.notes && <p className="text-xs text-slate-400 mt-0.5">{ev.notes}</p>}
                            </button>
                            <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-600 h-8 w-8 p-0 shrink-0" onClick={() => handleDeleteEvent(ev.id)} data-testid={`delete-event-${ev.id}`}><Trash2 className="w-4 h-4" /></Button>
                          </div>
                        ))}
                      </div>
                    ) : <p className="text-center py-8 text-slate-400 text-sm">No events scheduled</p>}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Files */}
              <TabsContent value="files" className="mt-4"><Card><CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Camera className="w-5 h-5" />Photos & Files</CardTitle></CardHeader><CardContent><PhotoUpload entityType="project" entityId={id} photos={photos} onPhotosChange={setPhotos} coverPhotoUrl={project?.cover_photo_url} onSetCoverPhoto={handleSetCoverPhoto} /></CardContent></Card></TabsContent>

              {/* Tasks */}
              <TabsContent value="tasks" className="mt-4" data-testid="project-tab-content-tasks">
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">Tasks</CardTitle>
                      <Button size="sm" className="bg-[#0F4C81] hover:bg-[#0D4270] h-9" onClick={() => setShowTaskModal(true)} data-testid="create-project-task-btn"><Plus className="w-4 h-4 mr-1" />New Task</Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {projectTasks.length > 0 ? (
                      <div className="space-y-2">
                        {projectTasks.map(task => (
                          <div key={task.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100" onClick={() => navigate(`/tasks?fromProject=${id}`)} data-testid={`project-task-${task.id}`}>
                            <div className={`w-2 h-2 rounded-full ${task.priority === "high" ? "bg-red-500" : task.priority === "medium" ? "bg-amber-500" : "bg-blue-500"}`} />
                            <div className="flex-1">
                              <p className="text-sm font-medium text-slate-700">{task.title}</p>
                              {task.due_date && <p className="text-xs text-slate-400">Due: {new Date(task.due_date).toLocaleDateString()}</p>}
                            </div>
                            <Badge variant={task.status === "completed" ? "default" : "outline"} className="text-xs capitalize">{task.status}</Badge>
                            <Badge variant="outline" className={`text-xs ${task.priority === "high" ? "border-red-300 text-red-600" : ""}`}>{task.priority}</Badge>
                          </div>
                        ))}
                      </div>
                    ) : <p className="text-center py-8 text-slate-400 text-sm">No tasks for this project</p>}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Inspection */}
              <TabsContent value="inspection" className="mt-4" data-testid="project-tab-content-inspection">
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">Inspection Reports</CardTitle>
                      <Button size="sm" className="bg-[#0F4C81] hover:bg-[#0D4270] h-9" onClick={handleCreateInspection} data-testid="create-project-inspection-btn"><Plus className="w-4 h-4 mr-1" />New Inspection</Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {inspections.length > 0 ? (
                      <div className="space-y-2">
                        {inspections.map(report => (
                          <div key={report.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer transition-colors" onClick={() => navigate(`/inspections?fromProject=${id}`)} data-testid={`project-inspection-${report.id}`}>
                            <div className="flex items-center gap-3">
                              <ClipboardCheck className="w-4 h-4 text-[#0F4C81]" />
                              <div>
                                <p className="text-sm font-medium text-slate-700">{report.report_number || "Draft"}</p>
                                <p className="text-xs text-slate-500">{report.property_address} - {report.inspection_date}</p>
                                <p className="text-xs text-slate-400">{report.items?.length || 0} items inspected</p>
                              </div>
                            </div>
                            <Badge variant={report.status === "completed" ? "default" : "outline"} className="text-xs capitalize">{report.status}</Badge>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-10 text-slate-400">
                        <ClipboardCheck className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                        <p className="text-sm">No inspections yet</p>
                        <p className="text-xs mt-1">Create an inspection report for this project</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Measure */}
              <TabsContent value="measure" className="mt-4" data-testid="project-tab-content-measure">
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">Roof Measurements</CardTitle>
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="outline" className="h-9" onClick={() => { loadAllReports(); setShowAttachReport(!showAttachReport); }} data-testid="attach-report-btn"><Link2 className="w-4 h-4 mr-1" />Attach Existing</Button>
                        <Button size="sm" className="bg-[#0F4C81] hover:bg-[#0D4270] h-9" onClick={handleCreateRoofReport} data-testid="create-roof-report-btn"><Plus className="w-4 h-4 mr-1" />New Report</Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* Attach existing report dropdown */}
                    {showAttachReport && (
                      <div className="mb-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
                        <p className="text-xs font-medium text-slate-500 mb-2">Select a report to attach:</p>
                        {allReports.length > 0 ? (
                          <div className="space-y-1 max-h-[200px] overflow-y-auto">
                            {allReports.map(r => (
                              <div key={r.id} className="flex items-center justify-between p-2 rounded hover:bg-white cursor-pointer transition-colors" onClick={() => handleAttachReport(r.id)} data-testid={`attach-report-${r.id}`}>
                                <div>
                                  <p className="text-sm font-medium text-slate-700">{r.name}</p>
                                  <p className="text-xs text-slate-400">{r.structures?.length || 0} structure(s) - {r.date}</p>
                                </div>
                                <Plus className="w-4 h-4 text-[#0F4C81]" />
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-slate-400">No unlinked reports available</p>
                        )}
                      </div>
                    )}
                    {roofReports.length > 0 ? (
                      <div className="space-y-2">
                        {roofReports.map(report => (
                          <div key={report.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer transition-colors group" onClick={() => setEmbeddedReportId(report.id)} data-testid={`roof-report-${report.id}`}>
                            <div className="flex items-center gap-3">
                              <Ruler className="w-4 h-4 text-[#0F4C81]" />
                              <div>
                                <p className="text-sm font-medium text-slate-700">{report.name}</p>
                                <p className="text-xs text-slate-500">{report.structures?.length || 0} structure(s) - {report.date}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant={report.status === "complete" ? "default" : "outline"} className="text-xs capitalize">{report.status}</Badge>
                              <button
                                type="button"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  if (!window.confirm(`Remove "${report.name}" from this project? The report will stay in your Measurements list — just unlinked from this project.`)) return;
                                  try {
                                    await axios.patch(`${API_URL}/roof-reports/${report.id}`, { project_id: null });
                                    setRoofReports(prev => prev.filter(r => r.id !== report.id));
                                    toast.success("Report removed from project");
                                  } catch {
                                    toast.error("Failed to remove report");
                                  }
                                }}
                                className="text-slate-300 hover:text-red-500 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-red-50"
                                title="Remove from this project (does not delete the report)"
                                data-testid={`detach-roof-report-${report.id}`}
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-10 text-slate-400">
                        <Ruler className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                        <p className="text-sm">No measurement reports yet</p>
                        <p className="text-xs mt-1">Create a roof report to track measurements</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Quote */}
              <TabsContent value="quote" className="mt-4" data-testid="project-tab-content-quote">
                <QuoteTabContent
                  projectId={id}
                  onQuotesChanged={() => fetchQuotes()}
                  contactId={project?.contact_id || ""}
                  projectQuotes={projectQuotes}
                  quoteTemplates={quoteTemplates}
                  roofReports={roofReports}
                  createQuote={createQuote}
                  deleteQuote={deleteQuote}
                  navigate={navigate}
                  fmtCurrency={fmtCurrency}
                />
              </TabsContent>

              {/* Order */}
              <TabsContent value="order" className="mt-4"><Card><CardContent className="pt-6"><TabPlaceholder icon={ShoppingCart} title="Purchase Orders" description="Purchase orders for materials" /></CardContent></Card></TabsContent>

              {/* Jobs */}
              <TabsContent value="jobs" className="mt-4"><Card><CardContent className="pt-6"><TabPlaceholder icon={Briefcase} title="Jobs" description="Job assignments and crew scheduling" /></CardContent></Card></TabsContent>

              {/* BILLING TAB */}
              <TabsContent value="billing" className="mt-4" data-testid="project-tab-content-billing">
                <div className="space-y-4">
                  {/* Billing Summary */}
                  {budget && (
                    <Card>
                      <CardHeader className="pb-3"><CardTitle className="text-base">Billing Summary</CardTitle></CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          {[
                            { label: "Contract Total", value: fmtCurrency(budget.contract_value), color: "text-slate-800", desc: "Sum of all approved quote values for this project" },
                            { label: "Total Invoiced", value: fmtCurrency(budget.total_invoiced), color: "text-blue-600", desc: "Total amount invoiced to the customer so far" },
                            { label: "Payments Received", value: fmtCurrency(budget.total_payments), color: "text-emerald-600", desc: "Total payments collected from the customer" },
                            { label: "Outstanding Balance", value: fmtCurrency(budget.outstanding_balance), color: "text-red-600", desc: "Contract total minus payments received — every dollar the customer still owes (invoiced AND uninvoiced)" },
                          ].map(s => (
                            <TooltipProvider key={s.label} delayDuration={200}>
                              <ShadTooltip>
                                <TooltipTrigger asChild>
                                  <div className="p-3 bg-slate-50 rounded-lg cursor-help">
                                    <p className="text-xs text-slate-500 mb-1">{s.label}</p>
                                    <p className={`text-lg font-bold ${s.color}`}>{s.value}</p>
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs text-xs"><p>{s.desc}</p></TooltipContent>
                              </ShadTooltip>
                            </TooltipProvider>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* True Profit — quick snapshot; edit costs in the Budget tab */}
                  {budget && (
                    <Card>
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-base">True Profit</CardTitle>
                          <Button size="sm" variant="outline" className="h-9" onClick={() => setActiveTab("budget")} data-testid="billing-jump-to-budget"><TrendingUp className="w-4 h-4 mr-1" />Manage Costs</Button>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                          {[
                            { label: "Revenue", value: fmtCurrency(budget.total_payments), color: "text-emerald-600", desc: "Payments actually received from the customer" },
                            { label: "Labor", value: fmtCurrency(budget.labor_costs), color: "text-blue-600", desc: "Total labor costs logged for this project" },
                            { label: "Materials", value: fmtCurrency(budget.material_costs), color: "text-amber-600", desc: "Total material costs logged for this project" },
                            { label: "Other Costs", value: fmtCurrency((budget.total_costs || 0) - (budget.labor_costs || 0) - (budget.material_costs || 0)), color: "text-slate-600", desc: "Costs that aren't labor or materials (equipment, subs, etc.)" },
                            { label: "True Profit", value: fmtCurrency((budget.total_payments || 0) - (budget.total_costs || 0)), color: ((budget.total_payments || 0) - (budget.total_costs || 0)) >= 0 ? "text-emerald-600" : "text-red-600", desc: "Revenue minus every cost logged — your take-home before overhead" },
                          ].map(s => (
                            <TooltipProvider key={s.label} delayDuration={200}>
                              <ShadTooltip>
                                <TooltipTrigger asChild>
                                  <div className="p-3 bg-slate-50 rounded-lg cursor-help">
                                    <p className="text-xs text-slate-500 mb-1">{s.label}</p>
                                    <p className={`text-lg font-bold ${s.color}`}>{s.value}</p>
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs text-xs"><p>{s.desc}</p></TooltipContent>
                              </ShadTooltip>
                            </TooltipProvider>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Invoices */}
                  <Card>
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base">Invoices</CardTitle>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-9 text-xs"
                            onClick={handleRefreshBillingStage}
                            data-testid="refresh-billing-stage-btn"
                            title="Re-check invoices & advance the project to Final Paid if everything's paid"
                          >
                            <RefreshCw className="w-3.5 h-3.5 mr-1" />
                            Refresh Stage
                          </Button>
                          <Button size="sm" className="bg-[#0F4C81] hover:bg-[#0D4270] h-9" onClick={() => { const autoQuote = projectQuotes.length === 1 ? projectQuotes[0].id : ""; setInvoiceForm(prev => ({...prev, quote_id: autoQuote})); setShowInvoiceModal(true); if (autoQuote) setTimeout(() => handleInvoiceTypeChange("deposit"), 150); }} data-testid="create-invoice-btn"><Plus className="w-4 h-4 mr-1" />New Invoice</Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {budget?.invoices?.length > 0 ? (
                        <div className="space-y-2">
                          {budget.invoices.map(inv => (
                            <div key={inv.id} className="bg-slate-50 rounded-lg overflow-hidden">
                              <div className="flex items-center justify-between p-3 cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => editingInvoiceId === inv.id ? setEditingInvoiceId(null) : startEditInvoice(inv)} data-testid={`invoice-row-${inv.id}`}>
                                <div>
                                  <p className="text-sm font-medium text-slate-700">{inv.invoice_number}{inv.title ? ` — ${inv.title}` : ""}</p>
                                  <p className="text-xs text-slate-500">{inv.description || "Invoice"} {inv.due_date && `- Due: ${new Date(inv.due_date).toLocaleDateString()}`}</p>
                                </div>
                                <div className="flex items-center gap-3">
                                  <span className="text-sm font-medium">{fmtCurrency(inv.amount)}</span>
                                  <Badge variant={inv.status === "paid" ? "default" : inv.status === "partial" ? "secondary" : "outline"} className="text-xs capitalize">{inv.status}</Badge>
                                  {inv.status !== "paid" && (
                                    <Button variant="ghost" size="sm" className="text-emerald-600 hover:text-emerald-700 h-7 w-7 p-0" title="Mark invoice fully paid" onClick={(e) => { e.stopPropagation(); handleMarkInvoicePaid(inv); }} data-testid={`invoice-markpaid-${inv.id}`}><CheckCircle2 className="w-3.5 h-3.5" /></Button>
                                  )}
                                  <Button variant="ghost" size="sm" className="text-blue-500 hover:text-blue-700 h-7 w-7 p-0" title="Regenerate from current quote total" onClick={(e) => { e.stopPropagation(); handleRegenerateInvoice(inv); }} data-testid={`invoice-regenerate-${inv.id}`}><RefreshCw className="w-3.5 h-3.5" /></Button>
                                  {inv.status !== "paid" && (
                                    <Button variant="ghost" size="sm" className="text-emerald-500 hover:text-emerald-700 h-7 w-7 p-0" title="Copy payment link" onClick={(e) => { e.stopPropagation(); const link = `${window.location.origin}/i/${inv.short_code || inv.id}`; navigator.clipboard.writeText(link); toast.success("Payment link copied!"); }} data-testid={`invoice-paylink-quick-${inv.id}`}><Link2 className="w-3.5 h-3.5" /></Button>
                                  )}
                                  <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-600 h-7 w-7 p-0" onClick={(e) => { e.stopPropagation(); handleDeleteItem("invoices", inv.id); }}><Trash2 className="w-3.5 h-3.5" /></Button>
                                </div>
                              </div>
                              {editingInvoiceId === inv.id && (
                                <div className="p-3 pt-0 border-t border-slate-200 space-y-3" data-testid={`invoice-edit-${inv.id}`}>
                                  {/* Title */}
                                  <div className="space-y-1">
                                    <Label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Invoice Title</Label>
                                    <div className="flex gap-1.5 flex-wrap mb-1.5">
                                      {invoiceTitlePresets.map(preset => (
                                        <button key={preset} type="button" className={`px-2 py-0.5 rounded text-[10px] border transition-colors ${editInvoiceForm.title === preset ? 'bg-[#0F4C81] text-white border-[#0F4C81]' : 'bg-white text-slate-500 border-slate-200 hover:border-[#0F4C81] hover:text-[#0F4C81]'}`} onClick={() => setEditInvoiceForm(prev => ({...prev, title: preset}))}>{preset}</button>
                                      ))}
                                    </div>
                                    <Input value={editInvoiceForm.title || ""} onChange={e => setEditInvoiceForm({...editInvoiceForm, title: e.target.value})} className="h-8 text-xs" placeholder="Custom title..." data-testid={`invoice-title-${inv.id}`} />
                                  </div>
                                  {/* Line Items */}
                                  <div className="space-y-2">
                                    <div className="flex items-center justify-between">
                                      <Label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Line Items</Label>
                                      <Button type="button" variant="ghost" size="sm" className="h-7 text-xs text-[#0F4C81]" onClick={addLineItem}><Plus className="w-3 h-3 mr-1" />Add Item</Button>
                                    </div>
                                    <div className="space-y-1.5">
                                      {(editInvoiceForm.line_items || []).map((li, idx) => (
                                        <div key={li.id || idx} className="grid grid-cols-[1fr_60px_90px_90px_28px] gap-1.5 items-center">
                                          <Input value={li.description} onChange={e => updateLineItem(idx, "description", e.target.value)} className="h-8 text-xs" placeholder="Description" />
                                          <Input type="number" value={li.quantity} onChange={e => updateLineItem(idx, "quantity", e.target.value)} className="h-8 text-xs text-center" placeholder="Qty" />
                                          <div className="relative"><span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">$</span><Input type="number" step="0.01" value={li.unit_price} onChange={e => updateLineItem(idx, "unit_price", e.target.value)} className="h-8 text-xs pl-4" placeholder="Price" /></div>
                                          <p className="text-xs font-medium text-right text-slate-700">${(parseFloat(li.amount) || 0).toFixed(2)}</p>
                                          <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600" onClick={() => removeLineItem(idx)}><Trash2 className="w-3 h-3" /></Button>
                                        </div>
                                      ))}
                                    </div>
                                    <div className="border-t border-slate-200 pt-2 space-y-1.5">
                                      <div className="flex justify-end items-center gap-2">
                                        <span className="text-xs text-slate-500">Subtotal</span>
                                        <span className="text-sm text-slate-600 w-24 text-right">{fmtCurrency((editInvoiceForm.line_items || []).reduce((s, it) => s + (parseFloat(it.amount) || 0), 0))}</span>
                                      </div>
                                      <div className="flex justify-end items-center gap-2">
                                        <Input value={editInvoiceForm.discount_label || ""} onChange={e => setEditInvoiceForm(prev => ({...prev, discount_label: e.target.value}))} className="h-7 text-xs w-36" placeholder="Discount label (e.g., Veteran)" data-testid={`invoice-discount-label-${inv.id}`} />
                                        <div className="relative w-24">
                                          <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">$</span>
                                          <Input type="number" step="0.01" min="0" value={editInvoiceForm.discount || ""} placeholder="0.00" className="h-7 text-xs pl-4" data-testid={`invoice-discount-${inv.id}`} onChange={e => {
                                            const disc = parseFloat(e.target.value) || 0;
                                            setEditInvoiceForm(prev => ({ ...prev, discount: disc, amount: _recomputeInvoiceTotal(prev.line_items, disc) }));
                                          }} />
                                        </div>
                                      </div>
                                      <div className="flex justify-end items-center gap-2">
                                        <Label className="text-xs font-bold text-slate-700 whitespace-nowrap">Amount Due</Label>
                                        <div className="relative w-24">
                                          <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">$</span>
                                          <Input type="number" step="0.01" min="0" value={editInvoiceForm.amount || ""} placeholder="0.00" className="h-7 text-xs pl-4 font-bold" data-testid={`invoice-total-${inv.id}`} onChange={e => {
                                            const amt = parseFloat(e.target.value) || 0;
                                            setEditInvoiceForm(prev => ({ ...prev, amount: amt }));
                                          }} />
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  {/* Meta Fields */}
                                  <div className="grid grid-cols-2 gap-3">
                                    <div className="space-y-1"><Label className="text-xs">Due Date</Label><DatePickerField value={editInvoiceForm.due_date} onChange={(v) => setEditInvoiceForm({...editInvoiceForm, due_date: v})} height="h-9" /></div>
                                    <div className="space-y-1"><Label className="text-xs">Status</Label>
                                      <Select value={editInvoiceForm.status} onValueChange={v => setEditInvoiceForm({...editInvoiceForm, status: v})}>
                                        <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="draft">Draft</SelectItem>
                                          <SelectItem value="sent">Sent</SelectItem>
                                          <SelectItem value="partial">Partial</SelectItem>
                                          <SelectItem value="paid">Paid</SelectItem>
                                          <SelectItem value="overdue">Overdue</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </div>
                                  </div>
                                  {/* Actions */}
                                  <div className="flex items-center justify-between pt-1">
                                    <div className="flex items-center gap-2">
                                      <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => handlePreviewInvoice(inv.id)} data-testid={`invoice-preview-${inv.id}`}><Eye className="w-3.5 h-3.5 mr-1" />Preview</Button>
                                      <Button variant="outline" size="sm" className="h-8 text-xs text-blue-600 border-blue-200 hover:bg-blue-50" onClick={() => handleSendInvoice(inv.id, inv.invoice_number)} data-testid={`invoice-send-${inv.id}`}><Send className="w-3.5 h-3.5 mr-1" />Send</Button>
                                      <Button variant="outline" size="sm" className="h-8 text-xs text-emerald-600 border-emerald-200 hover:bg-emerald-50" onClick={() => { const link = `${window.location.origin}/i/${inv.short_code || inv.id}`; navigator.clipboard.writeText(link); toast.success("Payment link copied!"); }} data-testid={`invoice-paylink-${inv.id}`}><Link2 className="w-3.5 h-3.5 mr-1" />Pay Link</Button>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Button variant="outline" size="sm" className="h-8" onClick={() => setEditingInvoiceId(null)}>Cancel</Button>
                                      <Button size="sm" className="h-8 bg-[#0F4C81] hover:bg-[#0D4270]" onClick={() => handleUpdateInvoice(inv.id)} data-testid={`invoice-save-${inv.id}`}>Save Changes</Button>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : <p className="text-center py-6 text-slate-400 text-sm">No invoices yet</p>}
                    </CardContent>
                  </Card>

                  {/* Payments */}
                  <Card>
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base">Payments</CardTitle>
                        <Button size="sm" className="bg-[#0F4C81] hover:bg-[#0D4270] h-9" onClick={() => setShowPaymentModal(true)} data-testid="create-payment-btn"><Plus className="w-4 h-4 mr-1" />New Payment</Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {budget?.payments?.length > 0 ? (
                        <div className="space-y-2">
                          {budget.payments.map(p => (
                            <div key={p.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                              <div>
                                <p className="text-sm font-medium text-slate-700">{fmtCurrency(p.amount)}</p>
                                <p className="text-xs text-slate-500">{p.method} {p.reference && `- ${p.reference}`} {p.date && `on ${p.date}`}</p>
                              </div>
                              <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-600 h-7 w-7 p-0" onClick={() => handleDeleteItem("payments", p.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                            </div>
                          ))}
                        </div>
                      ) : <p className="text-center py-6 text-slate-400 text-sm">No payments recorded</p>}
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* BUDGET TAB */}
              <TabsContent value="budget" className="mt-4" data-testid="project-tab-content-budget">
                <div className="space-y-4">
                  {budget && (
                    <Card>
                      <CardHeader className="pb-3"><CardTitle className="text-base">Project Analysis</CardTitle></CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          {[
                            { label: "Contract Value", value: fmtCurrency(budget.contract_value), icon: DollarSign, color: "text-slate-800", desc: "Total approved quote value for this project" },
                            { label: "Revenue", value: fmtCurrency(budget.total_payments), icon: TrendingUp, color: "text-emerald-600", desc: "Total payments received from the customer" },
                            { label: "Total Costs", value: fmtCurrency(budget.total_costs), icon: CreditCard, color: "text-red-600", desc: "Sum of all labor and material costs logged against this project" },
                            { label: "Labor Costs", value: fmtCurrency(budget.labor_costs), icon: User, color: "text-blue-600", desc: "Cost items categorized as labor (crew, subcontractors)" },
                            { label: "Material Costs", value: fmtCurrency(budget.material_costs), icon: ShoppingCart, color: "text-amber-600", desc: "Cost items categorized as materials (shingles, lumber, etc.)" },
                            { label: "Gross Profit", value: fmtCurrency(budget.gross_profit), icon: TrendingUp, color: budget.gross_profit >= 0 ? "text-emerald-600" : "text-red-600", desc: "Revenue minus Total Costs — your profit before overhead" },
                          ].map(s => (
                            <TooltipProvider key={s.label} delayDuration={200}>
                              <ShadTooltip>
                                <TooltipTrigger asChild>
                                  <div className="p-4 bg-slate-50 rounded-lg cursor-help">
                                    <div className="flex items-center gap-2 mb-1"><s.icon className="w-4 h-4 text-slate-400" /><p className="text-xs text-slate-500">{s.label}</p></div>
                                    <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs text-xs"><p>{s.desc}</p></TooltipContent>
                              </ShadTooltip>
                            </TooltipProvider>
                          ))}
                        </div>
                        <TooltipProvider delayDuration={200}>
                          <ShadTooltip>
                            <TooltipTrigger asChild>
                              <div className="mt-4 p-4 bg-[#0F4C81]/5 rounded-lg flex items-center gap-3 cursor-help">
                                <Percent className="w-5 h-5 text-[#0F4C81]" />
                                <div>
                                  <p className="text-sm text-slate-500">Gross Margin</p>
                                  <p className="text-2xl font-bold text-[#0F4C81]">{budget.gross_margin}%</p>
                                </div>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs text-xs"><p>Gross Profit divided by Revenue, expressed as a percentage. Higher is better.</p></TooltipContent>
                          </ShadTooltip>
                        </TooltipProvider>
                      </CardContent>
                    </Card>
                  )}

                  {/* Cost Line Items */}
                  <Card>
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base">Cost Line Items</CardTitle>
                        <Button size="sm" className="bg-[#0F4C81] hover:bg-[#0D4270] h-9" onClick={() => setShowCostModal(true)} data-testid="add-cost-btn"><Plus className="w-4 h-4 mr-1" />Add Cost</Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {budget?.costs?.length > 0 ? (
                        <div className="space-y-2">
                          {budget.costs.map(c => (
                            <div key={c.id} className="bg-slate-50 rounded-lg overflow-hidden">
                              {editingCostId === c.id ? (
                                <div className="p-3 space-y-3" data-testid={`cost-edit-${c.id}`}>
                                  <div className="grid grid-cols-1 md:grid-cols-[1fr_140px_140px] gap-2">
                                    <Input value={editCostForm.name} onChange={e => setEditCostForm({...editCostForm, name: e.target.value})} className="h-9 text-sm" placeholder="Name (e.g. Shingles)" />
                                    <div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">$</span><Input type="number" step="0.01" value={editCostForm.amount} onChange={e => setEditCostForm({...editCostForm, amount: e.target.value})} className="h-9 text-sm pl-6" placeholder="Amount" /></div>
                                    <Select value={editCostForm.cost_type} onValueChange={v => setEditCostForm({...editCostForm, cost_type: v})}>
                                      <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="material">Material</SelectItem>
                                        <SelectItem value="labor">Labor</SelectItem>
                                        <SelectItem value="equipment">Equipment</SelectItem>
                                        <SelectItem value="subcontractor">Subcontractor</SelectItem>
                                        <SelectItem value="other">Other</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  <Textarea value={editCostForm.notes} onChange={e => setEditCostForm({...editCostForm, notes: e.target.value})} rows={2} className="text-sm" placeholder="Notes (optional)" />
                                  <div className="flex justify-end gap-2">
                                    <Button size="sm" variant="outline" className="h-8" onClick={() => setEditingCostId(null)}>Cancel</Button>
                                    <Button size="sm" className="h-8 bg-[#0F4C81] hover:bg-[#0D4270]" onClick={handleUpdateCost} data-testid={`cost-save-${c.id}`}>Save</Button>
                                  </div>
                                </div>
                              ) : (
                                <div className="flex items-center justify-between p-3">
                                  <div>
                                    <p className="text-sm font-medium text-slate-700">{c.name}</p>
                                    <p className="text-xs text-slate-500 capitalize">{c.cost_type}{c.notes && ` - ${c.notes}`}</p>
                                  </div>
                                  <div className="flex items-center gap-3">
                                    <span className="text-sm font-medium">{fmtCurrency(c.amount)}</span>
                                    <Badge variant="outline" className="text-xs capitalize">{c.cost_type}</Badge>
                                    <Button variant="ghost" size="sm" className="text-[#0F4C81] hover:text-[#0D4270] h-7 w-7 p-0" onClick={() => startEditCost(c)} title="Edit cost" data-testid={`cost-edit-btn-${c.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                                    <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-600 h-7 w-7 p-0" onClick={() => handleDeleteItem("project-costs", c.id)} title="Delete cost"><Trash2 className="w-3.5 h-3.5" /></Button>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : <p className="text-center py-6 text-slate-400 text-sm">No costs tracked yet</p>}
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
            </Tabs>

            {/* Project Info Form */}
            <Card className="mt-6">
              <CardHeader className="pb-3"><CardTitle className="text-base">Project Information</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Street Address</Label>
                  <div className="relative"><MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" /><Input value={formData.project_address || ""} onChange={(e) => setFormData({ ...formData, project_address: e.target.value })} className="h-11 pl-10" placeholder="123 Main Street" data-testid="edit-project-address" /></div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>City</Label>
                    <Input value={formData.city || ""} onChange={(e) => setFormData({ ...formData, city: e.target.value })} className="h-11" placeholder="Charleston" data-testid="edit-project-city" />
                  </div>
                  <div className="space-y-2">
                    <Label>State</Label>
                    <Input value={formData.state || ""} onChange={(e) => setFormData({ ...formData, state: e.target.value })} className="h-11" placeholder="SC" data-testid="edit-project-state" />
                  </div>
                  <div className="space-y-2">
                    <Label>Zip</Label>
                    <Input value={formData.zip || ""} onChange={(e) => setFormData({ ...formData, zip: e.target.value })} className="h-11" placeholder="29401" data-testid="edit-project-zip" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Service Type</Label>
                    <Select value={formData.service_type || ""} onValueChange={(v) => setFormData({ ...formData, service_type: v })}>
                      <SelectTrigger className="h-11" data-testid="edit-service-type"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{serviceTypes.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Pipeline Stage</Label>
                    <Select value={formData.stage || ""} onValueChange={(v) => setFormData({ ...formData, stage: v })}>
                      <SelectTrigger className="h-11" data-testid="edit-pipeline-stage"><SelectValue placeholder="Select stage" /></SelectTrigger>
                      <SelectContent>
                        {pipelineCategories.map(cat => {
                          const catStages = pipelineStages.filter(s => s.category === cat.value);
                          if (!catStages.length) return null;
                          return (<div key={cat.value}><div className="px-2 py-1.5 text-xs font-semibold text-slate-400 uppercase">{cat.name}</div>{catStages.map(s => <SelectItem key={s.value} value={s.value}>{s.name}</SelectItem>)}</div>);
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Lead Source</Label>
                    <Select value={formData.lead_source || ""} onValueChange={(v) => setFormData({ ...formData, lead_source: v })}>
                      <SelectTrigger className="h-11"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{leadSources.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Referral Name</Label>
                    <Input value={formData.referral_name || ""} onChange={(e) => setFormData({ ...formData, referral_name: e.target.value })} className="h-11" placeholder="Who referred?" data-testid="edit-referral-name" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Classification</Label>
                    <Select value={formData.classification || ""} onValueChange={(v) => setFormData({ ...formData, classification: v })}>
                      <SelectTrigger className="h-11"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{classifications.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Estimated Value ($)</Label>
                    <div className="relative"><DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-500" /><Input type="number" value={formData.estimated_value ?? ''} onChange={(e) => setFormData({ ...formData, estimated_value: e.target.value })} className="h-11 pl-10" data-testid="edit-project-value" /></div>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea value={formData.notes || ""} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={3} data-testid="edit-project-notes" />
                </div>
                <div className="space-y-2 pt-1">
                  <Label className="text-slate-500 text-xs uppercase tracking-wider">Follow-Up Email Controls</Label>
                  <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2.5">
                    <input
                      type="checkbox"
                      id="skip-review-request"
                      checked={!!formData.skip_review_request}
                      onChange={(e) => setFormData({ ...formData, skip_review_request: e.target.checked })}
                      className="mt-1 h-4 w-4 accent-amber-600 cursor-pointer"
                      data-testid="edit-project-skip-review"
                    />
                    <label htmlFor="skip-review-request" className="text-sm cursor-pointer flex-1">
                      <span className="font-semibold text-amber-900">Skip review-request emails</span>
                      <span className="block text-xs text-amber-800/80 mt-0.5">
                        Turn on if they already left a review. Silences the ★ review reminder in the Paid &amp; Closed flow. Soft referral check-ins still send.
                      </span>
                    </label>
                  </div>
                  <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50/60 px-3 py-2.5">
                    <input
                      type="checkbox"
                      id="disable-customer-outreach"
                      checked={!!formData.disable_customer_outreach}
                      onChange={(e) => setFormData({ ...formData, disable_customer_outreach: e.target.checked })}
                      className="mt-1 h-4 w-4 accent-red-600 cursor-pointer"
                      data-testid="edit-project-disable-outreach"
                    />
                    <label htmlFor="disable-customer-outreach" className="text-sm cursor-pointer flex-1">
                      <span className="font-semibold text-red-900">Silence all follow-up emails</span>
                      <span className="block text-xs text-red-800/80 mt-0.5">
                        Nuclear switch for jobs that had friction. Blocks every review + referral touch across the entire nurture sequence.
                      </span>
                    </label>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* PAID & CLOSED CONFIRMATION MODAL — lets the admin pre-decide
          which follow-up emails should fire before the workflow starts. */}
      <Dialog open={paidClosedDialog.open} onOpenChange={(v) => { if (!v) setPaidClosedDialog({ open: false, pendingStage: "", skipReview: false, disableOutreach: false }); }}>
        <DialogContent className="max-w-md" data-testid="paid-closed-dialog">
          <DialogHeader>
            <DialogTitle>Move to Paid &amp; Closed?</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-600 mt-2 leading-relaxed">
            This kicks off the long-tail follow-up flow:
            <ul className="mt-2 mb-3 pl-4 list-disc text-slate-700 space-y-1 text-[13px]">
              <li><strong>Day 1</strong> — final review reminder</li>
              <li><strong>Every 30 days for 6 months</strong> — soft check-in / referral touches</li>
              <li><strong>Every 90 days after</strong> — quarterly stay-in-touch</li>
            </ul>
            Pick anything below that applies before we send the first note.
          </div>

          <div className="space-y-2 mt-2">
            <label htmlFor="pcd-skip-review" className="flex items-start gap-3 rounded-lg border border-slate-200 hover:border-amber-300 hover:bg-amber-50/40 px-3 py-2.5 cursor-pointer transition-colors">
              <input
                type="checkbox"
                id="pcd-skip-review"
                checked={paidClosedDialog.skipReview}
                onChange={(e) => setPaidClosedDialog(d => ({ ...d, skipReview: e.target.checked }))}
                className="mt-0.5 h-4 w-4 accent-amber-600 cursor-pointer"
                data-testid="pcd-skip-review"
              />
              <div className="flex-1">
                <div className="text-sm font-semibold text-slate-800">They already left a review</div>
                <div className="text-xs text-slate-500">Suppresses the ★ review reminder. Soft referral check-ins still send.</div>
              </div>
            </label>

            <label htmlFor="pcd-disable-outreach" className="flex items-start gap-3 rounded-lg border border-slate-200 hover:border-red-300 hover:bg-red-50/40 px-3 py-2.5 cursor-pointer transition-colors">
              <input
                type="checkbox"
                id="pcd-disable-outreach"
                checked={paidClosedDialog.disableOutreach}
                onChange={(e) => setPaidClosedDialog(d => ({ ...d, disableOutreach: e.target.checked, skipReview: e.target.checked ? true : d.skipReview }))}
                className="mt-0.5 h-4 w-4 accent-red-600 cursor-pointer"
                data-testid="pcd-disable-outreach"
              />
              <div className="flex-1">
                <div className="text-sm font-semibold text-slate-800">Job had issues — silence everything</div>
                <div className="text-xs text-slate-500">Nothing gets sent. No review pings, no referral touches, no quarterly check-ins.</div>
              </div>
            </label>
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setPaidClosedDialog({ open: false, pendingStage: "", skipReview: false, disableOutreach: false })} data-testid="pcd-cancel-btn">
              Cancel
            </Button>
            <Button
              className="bg-[#0F4C81] hover:bg-[#0D4270]"
              data-testid="pcd-confirm-btn"
              onClick={async () => {
                const stage = paidClosedDialog.pendingStage;
                const payload = {
                  ...formData,
                  stage,
                  skip_review_request: paidClosedDialog.skipReview,
                  disable_customer_outreach: paidClosedDialog.disableOutreach,
                };
                setFormData(prev => ({ ...prev, ...payload }));
                setPaidClosedDialog({ open: false, pendingStage: "", skipReview: false, disableOutreach: false });
                try {
                  const updated = await updateProject(id, payload);
                  setProject(updated);
                  toast.success("Moved to Paid & Closed");
                  fetchActivities("project", id).then(setActivities);
                } catch { toast.error("Failed to update stage"); }
              }}
            >
              Move &amp; Start Flow
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* EVENT MODAL */}
      <Dialog open={showEventModal} onOpenChange={(v) => { setShowEventModal(v); if (!v) { setEditingEventId(null); setEventForm({ title: "", event_type_id: "", start_date: "", start_time: "08:00", end_time: "17:00", notes: "", assigned_to: user?.id || "" }); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editingEventId ? "Edit Event" : "New Event"}</DialogTitle></DialogHeader>
          <form onSubmit={handleCreateEvent} className="space-y-4 mt-4">

            {/* Event Type select — auto-fills title + duration */}
            {eventTypes.length > 0 && (
              <div className="space-y-2">
                <Label>Event Type</Label>
                <select
                  value={eventForm.event_type_id || ""}
                  onChange={(e) => handleEventTypeChange(e.target.value)}
                  className="w-full h-11 rounded-md border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F4C81]/20"
                  data-testid="event-type-select"
                >
                  <option value="">— Custom (no type) —</option>
                  {eventTypes.map(et => (
                    <option key={et.id} value={et.id}>
                      {et.name}{et.duration_hours ? ` · ${et.duration_hours}h` : ""}{et.default_start_time ? ` · starts ${fmtTime12(et.default_start_time)}` : ""}
                    </option>
                  ))}
                </select>
                <p className="text-[11px] text-slate-400">Picking a type auto-fills title, start, and duration. Edit any of them below.</p>
              </div>
            )}

            <div className="space-y-2">
              <Label>Title *</Label>
              <Input value={eventForm.title} onChange={e => setEventForm({...eventForm, title: e.target.value})} required className="h-11" data-testid="event-title-input" />
            </div>

            <div className="space-y-2">
              <Label>Date *</Label>
              {/* Native date input — sidesteps the Popover-in-Dialog reliability
                  issues we hit on iOS Safari. */}
              <Input
                type="date"
                value={eventForm.start_date || ""}
                onChange={(e) => setEventForm({ ...eventForm, start_date: e.target.value })}
                className="h-11"
                data-testid="event-date-input"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Start</Label>
                <TimePicker12
                  value={eventForm.start_time}
                  onChange={(v) => {
                    // Auto-shift end_time to preserve the current event duration
                    // when the start time changes.
                    setEventForm(prev => {
                      const toMins = (t) => {
                        const [h, m] = (t || "").split(":").map(n => parseInt(n, 10));
                        return (Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(m) ? m : 0);
                      };
                      const fromMins = (n) => {
                        const wrapped = ((n % 1440) + 1440) % 1440;
                        return `${String(Math.floor(wrapped / 60)).padStart(2, "0")}:${String(wrapped % 60).padStart(2, "0")}`;
                      };
                      const oldStart = toMins(prev.start_time);
                      const oldEnd = toMins(prev.end_time);
                      const duration = oldEnd > oldStart ? oldEnd - oldStart : 60;
                      return { ...prev, start_time: v, end_time: fromMins(toMins(v) + duration) };
                    });
                  }}
                  testIdPrefix="proj-event-start"
                />
              </div>
              <div className="space-y-2">
                <Label>End</Label>
                <TimePicker12 value={eventForm.end_time} onChange={(v) => setEventForm({...eventForm, end_time: v})} testIdPrefix="proj-event-end" />
              </div>
            </div>

            {/* Assign to teammate */}
            {teamUsers.length > 0 && (
              <div className="space-y-2">
                <Label>Assign To</Label>
                <select
                  value={eventForm.assigned_to || ""}
                  onChange={(e) => setEventForm({ ...eventForm, assigned_to: e.target.value })}
                  className="w-full h-11 rounded-md border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F4C81]/20"
                  data-testid="event-assign-to-select"
                >
                  <option value="">— Unassigned —</option>
                  {teamUsers.map(u => (
                    <option key={u.id} value={u.id}>
                      {u.name || u.email}{u.role ? ` (${u.role})` : ""}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="space-y-2"><Label>Notes</Label><Textarea value={eventForm.notes} onChange={e => setEventForm({...eventForm, notes: e.target.value})} rows={2} /></div>
            <div className="flex gap-3"><Button type="button" variant="outline" className="flex-1 h-11" onClick={() => setShowEventModal(false)}>Cancel</Button><Button type="submit" className="flex-1 h-11 bg-[#0F4C81] hover:bg-[#0D4270]" data-testid="create-event-submit">{editingEventId ? "Save Changes" : "Create"}</Button></div>
          </form>
        </DialogContent>
      </Dialog>

      {/* TASK MODAL */}
      <Dialog open={showTaskModal} onOpenChange={setShowTaskModal}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>New Task</DialogTitle></DialogHeader>
          <form onSubmit={handleCreateTask} className="space-y-4 mt-4">
            <div className="space-y-2"><Label>Title *</Label><Input value={taskForm.title} onChange={e => setTaskForm({...taskForm, title: e.target.value})} required className="h-11" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Priority</Label><Select value={taskForm.priority} onValueChange={v => setTaskForm({...taskForm, priority: v})}><SelectTrigger className="h-11"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem></SelectContent></Select></div>
              <div className="space-y-2"><Label>Due Date</Label><DatePickerField value={taskForm.due_date} onChange={(v) => setTaskForm({...taskForm, due_date: v})} /></div>
            </div>
            <div className="space-y-2"><Label>Notes</Label><Textarea value={taskForm.notes} onChange={e => setTaskForm({...taskForm, notes: e.target.value})} rows={2} /></div>
            <div className="flex gap-3"><Button type="button" variant="outline" className="flex-1 h-11" onClick={() => setShowTaskModal(false)}>Cancel</Button><Button type="submit" className="flex-1 h-11 bg-[#0F4C81] hover:bg-[#0D4270]">Create</Button></div>
          </form>
        </DialogContent>
      </Dialog>

      {/* INVOICE MODAL */}
      <Dialog open={showInvoiceModal} onOpenChange={setShowInvoiceModal}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>New Invoice</DialogTitle></DialogHeader>
          <form onSubmit={handleCreateInvoice} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Invoice Type</Label>
              <Select value={invoiceForm.invoice_type} onValueChange={v => handleInvoiceTypeChange(v)}>
                <SelectTrigger className="h-11" data-testid="invoice-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="deposit">Deposit</SelectItem>
                  <SelectItem value="balance">Balance (Final)</SelectItem>
                  <SelectItem value="progress">Progress Payment</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {projectQuotes.length > 0 && (
              <div className="space-y-2"><Label>Link to Quote</Label><Select value={invoiceForm.quote_id || "none"} onValueChange={v => { setInvoiceForm(prev => ({...prev, quote_id: v === "none" ? "" : v})); if (v !== "none") setTimeout(() => handleInvoiceTypeChange(invoiceForm.invoice_type), 100); }}><SelectTrigger className="h-11"><SelectValue placeholder="Select quote" /></SelectTrigger><SelectContent><SelectItem value="none">None</SelectItem>{projectQuotes.map(q => { const tpl = q.template_id ? (quoteTemplates || []).find(t => t.id === q.template_id) : null; const tplName = tpl?.name || ""; return (<SelectItem key={q.id} value={q.id}>{q.quote_number || "Draft"}{tplName ? ` · ${tplName}` : ""}</SelectItem>); })}</SelectContent></Select></div>
            )}
            {invoiceForm.payments_applied > 0 && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                <p className="text-sm text-emerald-700 font-medium">Payments Already Received: {fmtCurrency(invoiceForm.payments_applied)}</p>
                {invoiceForm.invoice_type === "balance" && <p className="text-xs text-emerald-600 mt-1">This balance invoice reflects the remaining amount after prior payments.</p>}
              </div>
            )}
            {invoiceForm.invoice_type === "deposit" && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-blue-700 font-medium">Deposit</p>
                  <div className="inline-flex rounded-md border border-blue-200 p-0.5 bg-white">
                    <button type="button" onClick={() => setInvoiceForm(prev => ({ ...prev, deposit_mode: "percent" }))} className={`px-2 py-1 text-xs font-medium rounded ${invoiceForm.deposit_mode !== "flat" ? "bg-[#0F4C81] text-white" : "text-blue-700 hover:text-blue-900"}`} data-testid="invoice-deposit-mode-percent">Percent %</button>
                    <button type="button" onClick={() => setInvoiceForm(prev => ({ ...prev, deposit_mode: "flat", deposit_percent: null }))} className={`px-2 py-1 text-xs font-medium rounded ${invoiceForm.deposit_mode === "flat" ? "bg-[#0F4C81] text-white" : "text-blue-700 hover:text-blue-900"}`} data-testid="invoice-deposit-mode-flat">Flat $</button>
                  </div>
                </div>
                {invoiceForm.deposit_mode !== "flat" ? (
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-blue-700">Percentage of {invoiceForm.quote_id ? "linked quote total" : "the Amount below"}</p>
                    <div className="flex items-center gap-1">
                      <Input type="number" step="1" min="1" max="100" value={invoiceForm.deposit_percent ?? ""} placeholder="50" onChange={e => {
                        const pct = parseFloat(e.target.value) || 0;
                        const quoteTotal = invoiceForm.line_items?.reduce((s, li) => s + (parseFloat(li.amount) || 0), 0) || parseFloat(invoiceForm.amount) || 0;
                        const linkedQuote = projectQuotes.find(q => q.id === invoiceForm.quote_id);
                        const qTotal = linkedQuote ? Number(linkedQuote.approved_total ?? linkedQuote.total ?? 0) : quoteTotal;
                        const newAmount = qTotal > 0 ? Math.round(qTotal * (pct / 100) * 100) / 100 : parseFloat(invoiceForm.amount) || 0;
                        setInvoiceForm(prev => ({ ...prev, deposit_percent: pct, amount: newAmount || prev.amount }));
                      }} className="h-8 w-16 text-center text-sm" data-testid="deposit-pct-input" />
                      <span className="text-sm text-blue-600 font-medium">%</span>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-blue-700">Enter the flat dollar amount in the <strong>Amount</strong> field below. It ships as-is.</p>
                )}
              </div>
            )}
            {invoiceForm.line_items?.length > 0 && (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-1">
                <p className="text-xs font-medium text-slate-500 uppercase">Line Items</p>
                {invoiceForm.line_items.map((li, i) => (
                  <div key={i} className="flex justify-between text-sm"><span className="text-slate-600">{li.description}</span><span className="text-slate-800 font-medium">{fmtCurrency(li.amount)}</span></div>
                ))}
                <div className="flex justify-between text-sm font-bold border-t border-slate-300 pt-1 mt-1"><span>Subtotal</span><span>{fmtCurrency(invoiceForm.line_items.reduce((s, li) => s + (parseFloat(li.amount) || 0), 0))}</span></div>
              </div>
            )}
            <div className="space-y-2">
              <Label>Invoice Title</Label>
              <div className="flex gap-1.5 flex-wrap mb-1.5">
                {invoiceTitlePresets.map(preset => (
                  <button key={preset} type="button" className={`px-2.5 py-1 rounded text-xs border transition-colors ${invoiceForm.title === preset ? 'bg-[#0F4C81] text-white border-[#0F4C81]' : 'bg-white text-slate-500 border-slate-200 hover:border-[#0F4C81] hover:text-[#0F4C81]'}`} onClick={() => setInvoiceForm(prev => ({...prev, title: preset}))}>{preset}</button>
                ))}
              </div>
              <Input value={invoiceForm.title || ""} onChange={e => setInvoiceForm({...invoiceForm, title: e.target.value})} className="h-11" placeholder="Or type a custom title..." data-testid="invoice-title-input" />
            </div>
            <div className="space-y-2"><Label>Description / Notes</Label><Input value={invoiceForm.description} onChange={e => setInvoiceForm({...invoiceForm, description: e.target.value})} className="h-11" placeholder="e.g., Deposit, Final balance" data-testid="invoice-desc-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Amount Due *</Label><div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">$</span><Input type="number" step="0.01" value={invoiceForm.amount} onChange={e => setInvoiceForm({...invoiceForm, amount: e.target.value})} required className="h-11 pl-7" data-testid="invoice-amount-input" /></div></div>
              <div className="space-y-2"><Label>Due Date</Label><DatePickerField value={invoiceForm.due_date} onChange={(v) => setInvoiceForm({...invoiceForm, due_date: v})} testId="invoice-duedate-input" /></div>
            </div>
            <div className="flex gap-3"><Button type="button" variant="outline" className="flex-1 h-11" onClick={() => setShowInvoiceModal(false)}>Cancel</Button><Button type="submit" className="flex-1 h-11 bg-[#0F4C81] hover:bg-[#0D4270]" data-testid="create-invoice-submit">Create Invoice</Button></div>
          </form>
        </DialogContent>
      </Dialog>

      {/* PAYMENT MODAL */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Record Payment</DialogTitle></DialogHeader>
          <form onSubmit={handleCreatePayment} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Amount *</Label><div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">$</span><Input type="number" step="0.01" value={paymentForm.amount} onChange={e => setPaymentForm({...paymentForm, amount: e.target.value})} required className="h-11 pl-7" /></div></div>
              <div className="space-y-2"><Label>Method</Label><Select value={paymentForm.method} onValueChange={v => setPaymentForm({...paymentForm, method: v})}><SelectTrigger className="h-11"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="check">Check</SelectItem><SelectItem value="cash">Cash</SelectItem><SelectItem value="card">Card</SelectItem><SelectItem value="transfer">Transfer</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent></Select></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Reference #</Label><Input value={paymentForm.reference} onChange={e => setPaymentForm({...paymentForm, reference: e.target.value})} className="h-11" placeholder="Check #, Trans ID" /></div>
              <div className="space-y-2"><Label>Date</Label><DatePickerField value={paymentForm.date} onChange={(v) => setPaymentForm({...paymentForm, date: v})} /></div>
            </div>
            {budget?.invoices?.length > 0 && (
              <div className="space-y-2"><Label>Apply to Invoice</Label><Select value={paymentForm.invoice_id || "none"} onValueChange={v => setPaymentForm({...paymentForm, invoice_id: v === "none" ? "" : v})}><SelectTrigger className="h-11"><SelectValue placeholder="Optional" /></SelectTrigger><SelectContent><SelectItem value="none">None</SelectItem>{budget.invoices.map(inv => <SelectItem key={inv.id} value={inv.id}>{inv.invoice_number} ({fmtCurrency(inv.amount)})</SelectItem>)}</SelectContent></Select></div>
            )}
            {/* Bookkeeping correction. Writes the payment (as an
                `adjustment`) and updates the invoice, but sends the
                customer nothing and leaves the pipeline stage alone. */}
            <label
              className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 cursor-pointer"
              data-testid="payment-silent-label"
            >
              <input
                type="checkbox"
                checked={!!paymentForm.silent}
                onChange={(e) => setPaymentForm({ ...paymentForm, silent: e.target.checked })}
                className="mt-0.5 h-4 w-4 accent-amber-600"
                data-testid="payment-silent-checkbox"
              />
              <span className="text-sm text-amber-900">
                <span className="font-semibold">Record correction only, no customer notification</span>
                <span className="block text-xs text-amber-800 mt-0.5">
                  Saves the payment as an <b>adjustment</b> and updates the invoice total and status.
                  No receipt, no pipeline stage change.
                </span>
              </span>
            </label>
            <div className="flex gap-3"><Button type="button" variant="outline" className="flex-1 h-11" onClick={() => setShowPaymentModal(false)}>Cancel</Button><Button type="submit" className={`flex-1 h-11 ${paymentForm.silent ? "bg-amber-600 hover:bg-amber-700" : "bg-[#0F4C81] hover:bg-[#0D4270]"}`} data-testid="payment-submit-btn">{paymentForm.silent ? "Record Correction" : "Record Payment"}</Button></div>          </form>
        </DialogContent>
      </Dialog>

      {/* COST MODAL */}
      <Dialog open={showCostModal} onOpenChange={setShowCostModal}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Add Cost</DialogTitle></DialogHeader>
          <form onSubmit={handleCreateCost} className="space-y-4 mt-4">
            <div className="space-y-2"><Label>Name *</Label><Input value={costForm.name} onChange={e => setCostForm({...costForm, name: e.target.value})} required className="h-11" placeholder="e.g., Shingles, Labor crew" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Amount *</Label><div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">$</span><Input type="number" step="0.01" value={costForm.amount} onChange={e => setCostForm({...costForm, amount: e.target.value})} required className="h-11 pl-7" /></div></div>
              <div className="space-y-2"><Label>Type</Label><Select value={costForm.cost_type} onValueChange={v => setCostForm({...costForm, cost_type: v})}><SelectTrigger className="h-11"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="material">Material</SelectItem><SelectItem value="labor">Labor</SelectItem><SelectItem value="equipment">Equipment</SelectItem><SelectItem value="subcontractor">Subcontractor</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent></Select></div>
            </div>
            <div className="space-y-2"><Label>Notes</Label><Textarea value={costForm.notes} onChange={e => setCostForm({...costForm, notes: e.target.value})} rows={2} /></div>
            <div className="flex gap-3"><Button type="button" variant="outline" className="flex-1 h-11" onClick={() => setShowCostModal(false)}>Cancel</Button><Button type="submit" className="flex-1 h-11 bg-[#0F4C81] hover:bg-[#0D4270]">Add Cost</Button></div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Invoice Preview Modal */}
      <Dialog open={!!invoicePreview} onOpenChange={(open) => !open && setInvoicePreview(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Invoice Preview</DialogTitle></DialogHeader>
          {invoicePreview && (
            <div className="mt-2 bg-white" data-testid="invoice-preview-modal">
              {/* Invoice Header */}
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-2xl font-extrabold text-slate-800">{invoicePreview.company?.name || "Your Company"}</h2>
                  {invoicePreview.company?.address && <p className="text-sm text-slate-500">{invoicePreview.company.address}</p>}
                  {invoicePreview.company?.phone && <p className="text-sm text-slate-500">{invoicePreview.company.phone}</p>}
                  {invoicePreview.company?.email && <p className="text-sm text-slate-500">{invoicePreview.company.email}</p>}
                </div>
                <div className="text-right">
                  <p className="text-3xl font-extrabold text-[#0F4C81]">INVOICE</p>
                  <p className="text-sm font-bold text-slate-700 mt-1">{invoicePreview.invoice_number}</p>
                  {invoicePreview.title && <p className="text-sm text-slate-600">{invoicePreview.title}</p>}
                  <Badge variant={invoicePreview.status === "paid" ? "default" : "outline"} className="mt-1 capitalize">{invoicePreview.status}</Badge>
                </div>
              </div>

              {/* Bill To */}
              <div className="grid grid-cols-2 gap-6 mb-6 pb-4 border-b">
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-1">Bill To</p>
                  <p className="text-sm font-bold text-slate-800">{invoicePreview.contact?.name || "Customer"}</p>
                  {invoicePreview.project_address && <p className="text-sm text-slate-500">{invoicePreview.project_address}</p>}
                  {invoicePreview.contact?.email && <p className="text-sm text-slate-500">{invoicePreview.contact.email}</p>}
                  {invoicePreview.contact?.phone && <p className="text-sm text-slate-500">{invoicePreview.contact.phone}</p>}
                </div>
                <div className="text-right">
                  <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-1">Details</p>
                  {invoicePreview.due_date && <p className="text-sm text-slate-600">Due: <span className="font-semibold">{new Date(invoicePreview.due_date).toLocaleDateString()}</span></p>}
                  {invoicePreview.created_at && <p className="text-sm text-slate-600">Issued: {new Date(invoicePreview.created_at).toLocaleDateString()}</p>}
                </div>
              </div>

              {/* Line Items Table */}
              <table className="w-full mb-6">
                <thead>
                  <tr className="border-b-2 border-slate-200">
                    <th className="text-left text-[10px] uppercase tracking-widest text-slate-400 font-bold py-2">Description</th>
                    <th className="text-center text-[10px] uppercase tracking-widest text-slate-400 font-bold py-2 w-16">Qty</th>
                    <th className="text-right text-[10px] uppercase tracking-widest text-slate-400 font-bold py-2 w-24">Price</th>
                    <th className="text-right text-[10px] uppercase tracking-widest text-slate-400 font-bold py-2 w-24">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {(invoicePreview.line_items || [{ description: invoicePreview.description, quantity: 1, unit_price: invoicePreview.amount, amount: invoicePreview.amount }]).map((li, i) => (
                    <tr key={i} className="border-b border-slate-100">
                      <td className="py-2.5 text-sm text-slate-700">{li.description || "—"}</td>
                      <td className="py-2.5 text-sm text-center text-slate-500">{li.quantity || 1}</td>
                      <td className="py-2.5 text-sm text-right text-slate-500">${parseFloat(li.unit_price || 0).toFixed(2)}</td>
                      <td className="py-2.5 text-sm text-right font-semibold text-slate-700">${parseFloat(li.amount || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Total */}
              {(() => {
                const lineTotal = (invoicePreview.line_items || []).reduce((s, li) => s + (parseFloat(li.amount) || 0), 0);
                const invoiceAmount = parseFloat(invoicePreview.amount || 0);
                const discount = parseFloat(invoicePreview.discount || 0);
                const depositPct = invoicePreview.deposit_percent;
                const paymentsApplied = parseFloat(invoicePreview.payments_applied || 0);
                const hasDeposit = depositPct && depositPct < 100 && lineTotal > invoiceAmount + discount + paymentsApplied;
                const isBalance = invoicePreview.invoice_type === "balance";
                return (
                  <div className="flex justify-end">
                    <div className="w-64 border-t-2 border-slate-800 pt-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-slate-600">Subtotal</span>
                        <span className="text-sm text-slate-700">{fmtCurrency(lineTotal)}</span>
                      </div>
                      {hasDeposit && (
                        <div className="flex justify-between mt-1">
                          <span className="text-sm text-slate-600">Deposit ({depositPct}%)</span>
                          <span className="text-sm text-slate-700">{fmtCurrency(round2(lineTotal * depositPct / 100))}</span>
                        </div>
                      )}
                      {paymentsApplied > 0 && (
                        <div className="flex justify-between mt-1">
                          <span className="text-sm text-slate-600">{isBalance ? "Prior Payments" : "Payments Applied"}</span>
                          <span className="text-sm text-emerald-600">-{fmtCurrency(paymentsApplied)}</span>
                        </div>
                      )}
                      {discount > 0 && (
                        <div className="flex justify-between mt-1">
                          <span className="text-sm text-slate-600">{invoicePreview.discount_label || "Discount"}</span>
                          <span className="text-sm text-red-500">-{fmtCurrency(discount)}</span>
                        </div>
                      )}
                      <div className="flex justify-between mt-1">
                        <span className="text-sm font-bold text-slate-800">Total Due</span>
                        <span className="text-xl font-extrabold text-[#0F4C81]">{fmtCurrency(invoiceAmount)}</span>
                      </div>
                      {parseFloat(invoicePreview.paid_amount || 0) > 0 && (
                        <>
                          <div className="flex justify-between mt-1"><span className="text-xs text-slate-500">Paid</span><span className="text-sm text-emerald-600">-{fmtCurrency(parseFloat(invoicePreview.paid_amount))}</span></div>
                          <div className="flex justify-between mt-1 border-t pt-1"><span className="text-sm font-bold">Balance</span><span className="text-lg font-extrabold text-red-600">{fmtCurrency(invoiceAmount - parseFloat(invoicePreview.paid_amount || 0))}</span></div>
                        </>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Actions */}
              <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
                <Button variant="outline" onClick={() => setInvoicePreview(null)}>Close</Button>
                <Button className="bg-[#0F4C81] hover:bg-[#0D4270]" onClick={() => { handleSendInvoice(invoicePreview.id, invoicePreview.invoice_number); setInvoicePreview(null); }}><Send className="w-4 h-4 mr-1.5" />Send to Customer</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Mobile-only fixed save bar — the in-header Save button is easy to
          miss on small phones once you scroll into form fields. This sticky
          bottom bar guarantees Save is always one tap away on mobile.
          Hidden ≥sm because desktop already has a prominent header Save. */}
      <div className="fixed bottom-0 inset-x-0 z-40 bg-white border-t border-slate-200 px-4 py-3 flex items-center gap-2 shadow-[0_-4px_12px_rgba(0,0,0,0.06)] sm:hidden" data-testid="mobile-save-bar">
        <Button
          className="flex-1 h-12 text-base font-semibold bg-[#0F4C81] hover:bg-[#0D4270]"
          onClick={handleSave}
          disabled={saving}
          data-testid="mobile-save-project-btn"
        >
          {saving ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Save className="w-5 h-5 mr-2" />}
          {saving ? "Saving…" : "Save Changes"}
        </Button>
        <Button
          variant="outline"
          className="h-12 w-12 p-0 border-red-300 text-red-600 hover:bg-red-50"
          aria-label="Delete project"
          data-testid="mobile-delete-project-btn"
          onClick={async () => {
            if (!window.confirm(`Delete this project? It will be moved to Trash and can be restored from there.`)) return;
            try {
              await deleteProject(id);
              toast.success("Project moved to Trash");
              goBack("/projects");
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Failed to delete project");
            }
          }}
        >
          <Trash2 className="w-5 h-5" />
        </Button>
      </div>
      <SendInvoiceDialog
        open={sendInvoiceDialog.open}
        onOpenChange={(v) => setSendInvoiceDialog(prev => ({ ...prev, open: v }))}
        invoiceId={sendInvoiceDialog.invoiceId}
        invoiceNumber={sendInvoiceDialog.invoiceNumber}
        defaultEmail={contact?.email || ""}
        onSent={loadBudget}
      />
      {/* Full-screen measurement editor mounted inline so the user stays
          inside the project instead of getting routed to /measurements. */}
      <Dialog open={Boolean(embeddedReportId)} onOpenChange={(v) => { if (!v) { setEmbeddedReportId(null); loadRoofReports(); } }}>
        <DialogContent className="max-w-[95vw] w-[95vw] h-[95vh] max-h-[95vh] p-0 overflow-hidden">
          <div className="h-full overflow-y-auto p-4">
            {embeddedReportId && (
              <MeasurementCalculator
                embeddedReportId={embeddedReportId}
                onClose={() => { setEmbeddedReportId(null); loadRoofReports(); }}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
